import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { RotateCcw, X, Check, RefreshCcw, Copy } from 'lucide-react';
import type { UnifiedContentItem } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { socialMediaApi } from '../api';
import { useBrandSettings } from '../hooks/useBrandSettings';
import { useHolderSwitcher } from '@/components/HolderSwitcher';
import { toast } from '@/components/ui/use-toast';
import {
  buildMarkdownPreview,
  type IdentityState as BrandIdentityState,
  type VoiceState as BrandVoiceState,
  type ColorSwatch as BrandColorSwatch,
} from './BrandSettingsPage';
import { CalendarIcon, FloppyIcon, LightbulbIcon, PenToolIcon } from '../components/Icons';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

type FlowStep =
  | 'start'
  | 'awaiting-ideas-direction'
  | 'awaiting-ideas-audience'
  | 'awaiting-ideas-style'
  | 'awaiting-ideas-hook'
  | 'awaiting-ideas-inspiration'
  | 'awaiting-ideas-constraints'
  | 'awaiting-ideas-confirm'
  | 'awaiting-calendar-timeframe' // step 1: timeframe + cadence (unchanged)
  | 'awaiting-calendar-platforms' // step 2: primary platform + repurpose toggle
  | 'awaiting-calendar-goal' // step 3: primary goal + secondary goal input
  | 'awaiting-calendar-pillars' // step 4: pillar weights + intent per pillar
  | 'awaiting-calendar-content-type' // step 5: content type distribution sliders
  | 'awaiting-calendar-hook-strategy' // step 6: hook strategy priority ranking
  | 'awaiting-calendar-interaction' // step 7: interaction style multi-select
  | 'awaiting-calendar-variation' // step 8: variation engine level
  | 'awaiting-calendar-inspiration' // step 9: source input
  | 'awaiting-calendar-constraints' // step 10: include/avoid multi-select
  | 'awaiting-calendar-confirm'
  | 'generating'
  | 'done';

type OutputMode = 'empty' | 'ideas' | 'calendar';

interface ContentIdea {
  hook: string;
  concept: string;
  angle: string;
  format: 'tweet' | 'thread' | 'carousel' | 'video';
  notes?: string;
  whyItWorks: string;
  pillar: string;
}

interface IdeaCardData {
  id: string;
  idea: ContentIdea;
  saved: boolean;
  isRegenerating: boolean;
}

interface IdeasFlowState {
  direction: string;
  directionOverride: string;
  audiencePain: string[];
  audiencePainOverride: string;
  style: string[];
  hookPreference: string[];
  inspirationMode: 'use' | 'skip';
  inspirationSeed: string;
  selectedInspirationIds: string[];
  constraints: string;
}

interface CalendarPostData {
  id: string;
  day: string;
  time: string;
  pillar: string;
  hook: string;
  copy: string;
  format: string;
  saved: boolean;
}

type MessageElement =
  | 'goal-cards'
  | 'ideas-chips-direction'
  | 'ideas-chips-audience'
  | 'ideas-chips-style'
  | 'ideas-chips-hook'
  | 'ideas-chips-inspiration'
  | 'ideas-constraints-input'
  | 'ideas-confirm'
  | 'platform-picker'
  | 'calendar-setup'
  | 'follow-up-chips-ideas'
  | 'follow-up-chips-calendar'
  | 'calendar-step-1'
  // Refactored calendar flow elements
  | 'calendar-platform-primary' // step 2: primary platform + repurpose
  | 'calendar-goal-secondary' // step 3: primary + secondary goal
  | 'calendar-chips-pillars' // step 4: pillar weights + intent
  | 'calendar-content-type' // step 5: content type distribution
  | 'calendar-hook-strategy' // step 6: hook priority ranking
  | 'calendar-interaction-style' // step 7: interaction style
  | 'calendar-variation' // step 8: variation level
  | 'calendar-chips-inspiration' // step 9: source input
  | 'calendar-constraints-ia' // step 10: include/avoid constraints
  | 'calendar-confirm'
  // Legacy elements (no longer emitted but kept for type safety)
  | 'calendar-chips-platforms'
  | 'calendar-chips-goal'
  | 'calendar-chips-content-style'
  | 'calendar-chips-engagement-style'
  | 'topic-input'
  | 'calendar-constraints-input';

interface PillarWeight {
  name: string;
  weight: number;
}

interface CalendarPlanState {
  // Step 1: Timeframe + Cadence (unchanged)
  timeframeValue: number;
  timeframeUnit: 'days' | 'weeks' | 'months';
  cadenceType: 'per_week' | 'daily';
  cadenceValue: number;
  // Step 2: Platform
  primaryPlatform: string;
  repurpose: boolean;
  platforms: string[]; // kept for legacy compat; derived from primaryPlatform
  // Step 3: Goals
  goal: string;
  secondaryGoal: string;
  // Step 4: Content Pillars
  pillarMode: 'default' | 'adjust';
  pillarWeights: PillarWeight[];
  pillarIntents: Record<string, string>; // pillar name → intent (Teach/Entertain/Provoke/Document)
  // Step 5: Content Type Distribution
  contentTypeDistribution: Record<string, number>;
  // Step 6: Hook Strategy
  hookStrategy: string[]; // ordered by priority
  // Step 7: Interaction Style
  interactionStyle: string[];
  // Step 8: Variation
  variationLevel: 'low' | 'medium' | 'high';
  // Step 9: Source Input (inspiration)
  inspirationMode: 'use' | 'skip';
  inspirationSeed: string;
  selectedInspirationIds: string[];
  // Step 10: Constraints
  constraintsInclude: string[];
  constraintsAvoid: string[];
  constraints: string; // free-text note
  // Legacy (kept for API compat)
  contentStyle: string[];
  engagementStyle: string[];
}

interface ChatMessageData {
  id: string;
  role: 'ai' | 'user';
  content: string;
  isTyping?: boolean;
  element?: MessageElement;
}

type BrandSettingsShape = {
  identity?: {
    brandName?: string | null;
    industry?: string | null;
    tagline?: string | null;
    website?: string | null;
  };
  voice?: {
    personaArchetype?: string | null;
    toneSpectrum?: {
      formality?: number | null;
      humor?: number | null;
      boldness?: number | null;
      empathy?: number | null;
    } | null;
    pillarWeights?: Record<string, number>;
    voiceDescriptors?: string[] | null;
    avoidDescriptors?: string[] | null;
    brandDescription?: string | null;
    targetAudience?: string | null;
    contentPillars?: string | null;
    personaVoice?: string | null;
    wordsToUse?: string | null;
    wordsToNeverUse?: string | null;
    brandSpecificTerms?: string | null;
    hashtagStrategy?: string | null;
  };
  palette?: Array<{ hex: string; name: string }> | null;
} | null;

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const NAMED_PILLAR_COLORS: Record<string, { bg: string; text: string; hex: string }> = {
  'Product updates': { bg: '#EEE9FF', text: '#534AB7', hex: '#534AB7' },
  'Tips & tricks': { bg: '#E1F5EE', text: '#0F6E56', hex: '#0F6E56' },
  'Behind the scenes': { bg: '#FEF3E2', text: '#854F0B', hex: '#854F0B' },
  Community: { bg: '#FDECEA', text: '#993C1D', hex: '#993C1D' },
  'Thought leadership': { bg: '#F0F4FF', text: '#2D4FCA', hex: '#2D4FCA' },
  Education: { bg: '#E0F7FA', text: '#006064', hex: '#00ACC1' },
  'Case studies': { bg: '#FCE4EC', text: '#880E4F', hex: '#C2185B' },
  Testimonials: { bg: '#E8F5E9', text: '#1B5E20', hex: '#43A047' },
  Entertainment: { bg: '#FFF3E0', text: '#E65100', hex: '#FB8C00' },
  News: { bg: '#F3E5F5', text: '#4A148C', hex: '#8E24AA' },
};

const FALLBACK_PILLAR_PALETTE = [
  { bg: '#E8F4FD', text: '#1565A8', hex: '#1E88E5' },
  { bg: '#FCE4EC', text: '#880E4F', hex: '#C2185B' },
  { bg: '#E8F5E9', text: '#1B5E20', hex: '#43A047' },
  { bg: '#FFF3E0', text: '#E65100', hex: '#FB8C00' },
  { bg: '#F3E5F5', text: '#4A148C', hex: '#8E24AA' },
  { bg: '#E0F7FA', text: '#006064', hex: '#00ACC1' },
  { bg: '#FFF8E1', text: '#F57F17', hex: '#FFB300' },
  { bg: '#FDECEA', text: '#993C1D', hex: '#D32F2F' },
];

const IDEAS_DIRECTIONS = [
  'My expertise',
  'My product / service',
  'My journey',
  "My audience's problems",
  'Trends in my space',
  'Opinions / hot takes',
  'Mixed',
];

const IDEAS_AUDIENCE_PAIN = [
  'Beginner struggles',
  'Advanced challenges',
  'Scaling / growth issues',
  'Technical problems',
  'Mindset / motivation',
  'Industry insights',
  'Mistakes / failures',
  'Misconceptions',
];

const IDEAS_STYLES = [
  'Educational',
  'Storytelling',
  'Opinionated',
  'Entertaining',
  'Tactical / actionable',
  'Personal',
  'Analytical',
  'Short-form punchy',
];

const IDEAS_HOOK_PREFERENCES = [
  'Strong opinions',
  'Questions',
  'Contrarian takes',
  'Personal story hooks',
  '"I learned this the hard way"',
  'Data / insight-driven',
  'Curiosity gaps',
  'Relatable moments',
  'Direct statements',
  'Controversial takes',
];

const PLATFORMS = ['Twitter', 'LinkedIn', 'Instagram', 'Facebook', 'TikTok'];

// ── New calendar flow constants ───────────────────────────────────────────────

const CALENDAR_PLATFORMS_PRIMARY = ['TikTok', 'Instagram', 'X', 'LinkedIn', 'YouTube'];

const CALENDAR_GOALS_PRIMARY = ['Engagement', 'Growth', 'Authority', 'Conversion'];

const PILLAR_INTENTS = ['Teach', 'Entertain', 'Provoke', 'Document'];

const CONTENT_TYPE_LABELS = [
  'Story / Narrative',
  'Insight / Lessons',
  'Opinion / Hot takes',
  'Tactical / How-to',
  'Relatable / Observational',
] as const;

type ContentTypeName = (typeof CONTENT_TYPE_LABELS)[number];

const DEFAULT_CONTENT_TYPE_DISTRIBUTION: Record<ContentTypeName, number> = {
  'Story / Narrative': 20,
  'Insight / Lessons': 20,
  'Opinion / Hot takes': 20,
  'Tactical / How-to': 20,
  'Relatable / Observational': 20,
};

const HOOK_STRATEGIES = [
  'Contrarian / Hot take',
  'Curiosity / Teaser',
  'Specific / Relatable',
  'Authority / Results',
  'Question-based',
  'Narrative / Story-led',
  'Shock / Unexpected',
  'Problem-first',
];

const INTERACTION_STYLES = [
  'Passive (strong statement)',
  'Light (save/share/follow)',
  'Conversational (comments)',
  'Debate-driven (opinions)',
  'Authority (no CTA)',
  'Aggressive (DM/click/convert)',
  'Mix (rotate all)',
];

const CONSTRAINTS_INCLUDE_OPTIONS = [
  'Personal stories',
  'Data / metrics',
  'Controversy',
  'Trends',
  'Educational depth',
  'Behind-the-scenes',
  'Opinions',
];

const CONSTRAINTS_AVOID_OPTIONS = [
  'Overly technical',
  'Clickbait',
  'Long-form',
  'Repetitive topics',
  'Generic advice',
];

const VARIATION_DESCRIPTIONS = {
  low: {
    label: 'Low',
    desc: 'Consistent tone and structure. Predictable, trust-building. Minimal experimentation.',
    hint: 'Best for established brands or compliance-sensitive audiences.',
  },
  medium: {
    label: 'Medium',
    desc: 'Balanced mix — mostly consistent with planned spikes. Moderate variation in hooks and formats.',
    hint: 'Works for most growing accounts. Recommended default.',
  },
  high: {
    label: 'High',
    desc: 'Aggressive hook diversity, strong emotional contrast, and format experimentation.',
    hint: 'Best for growth sprints or brands prioritizing reach over consistency.',
  },
} as const;

// const IDEAS_FOLLOW_UP = [
//   'Make them more casual',
//   'Focus only on product pillar',
//   'Add a campaign angle',
//   'Make them shorter',
//   'Add more humor',
// ];

// const CALENDAR_FOLLOW_UP = [
//   'Swap Tuesday and Thursday',
//   'Add weekend posts',
//   'Focus more on Tips pillar',
//   'Make cadence lighter',
// ];

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function genId() {
  return Math.random().toString(36).slice(2, 11);
}

function getPillarColor(pillar: string): { bg: string; text: string; hex: string } {
  if (NAMED_PILLAR_COLORS[pillar]) return NAMED_PILLAR_COLORS[pillar];
  const hash = [...pillar].reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return FALLBACK_PILLAR_PALETTE[hash % FALLBACK_PILLAR_PALETTE.length];
}

/**
 * Adapts the API-shaped BrandSettingsShape into the args expected by
 * buildMarkdownPreview from BrandSettingsPage, producing a rich brand
 * context string for display in the AI context modal.
 */
function buildBrandContextMarkdown(settings: BrandSettingsShape): string {
  if (!settings) return '';

  const identity: BrandIdentityState = {
    brandName: settings.identity?.brandName ?? '',
    industry: settings.identity?.industry ?? '',
    tagline: settings.identity?.tagline ?? '',
    website: settings.identity?.website ?? '',
  };

  const voice: BrandVoiceState = {
    personaArchetype: settings.voice?.personaArchetype ?? '',
    toneSpectrum: {
      formality: settings.voice?.toneSpectrum?.formality ?? 50,
      humor: settings.voice?.toneSpectrum?.humor ?? 50,
      boldness: settings.voice?.toneSpectrum?.boldness ?? 50,
      empathy: settings.voice?.toneSpectrum?.empathy ?? 50,
    },
    voiceDescriptors: settings.voice?.voiceDescriptors ?? [],
    avoidDescriptors: settings.voice?.avoidDescriptors ?? [],
    brandDescription: settings.voice?.brandDescription ?? '',
    targetAudience: settings.voice?.targetAudience ?? '',
    contentPillars: settings.voice?.contentPillars ?? '',
    pillarWeights: settings.voice?.pillarWeights ?? {},
    postStructure: '',
    differentiationFraming: '',
    personaVoice: settings.voice?.personaVoice ?? '',
    signOffStyle: '',
    defaultCTAType: '',
    wordsToUse: settings.voice?.wordsToUse ?? '',
    wordsToNeverUse: settings.voice?.wordsToNeverUse ?? '',
    brandSpecificTerms: settings.voice?.brandSpecificTerms ?? '',
    hashtagStrategy: settings.voice?.hashtagStrategy ?? '',
    emojiUsage: '',
    postLengthPreference: '',
    useLineBreaks: false,
    includeQuestions: false,
    referenceBrandColor: false,
  };

  const palette: BrandColorSwatch[] = settings.palette ?? [];

  return buildMarkdownPreview(identity, voice, palette);
}

function highlightMarkdown(md: string): string {
  return md
    .split('\n')
    .map((line) => {
      if (/^#{1,3} /.test(line)) return `<span style="color:#FAC775">${line}</span>`;
      if (/^\*\*[^*]+:\*\*/.test(line)) {
        return line.replace(
          /(\*\*[^*]+:\*\*)(.*)/,
          (_, label: string, rest: string) =>
            `<span style="color:#7F77DD">${label}</span><span style="color:#C9C7C1">${rest}</span>`,
        );
      }
      if (/^- /.test(line))
        return `<span style="color:#9FE1CB">-</span><span style="color:#C9C7C1">${line.slice(1)}</span>`;
      if (/^\*[^*]/.test(line)) return `<span style="color:#5F5E5A">${line}</span>`;
      if (/^---$/.test(line.trim())) return `<span style="color:#3A3835">${line}</span>`;
      return `<span style="color:#C9C7C1">${line}</span>`;
    })
    .join('\n');
}

function reorderByPriority(chips: string[], priorities: string[]): string[] {
  const prioritySet = new Set(priorities);
  return [
    ...priorities.filter((p) => chips.includes(p)),
    ...chips.filter((c) => !prioritySet.has(c)),
  ];
}

function getBrandAwareIdeasDirections(brandSettings: BrandSettingsShape): string[] {
  const archetype = brandSettings?.voice?.personaArchetype ?? '';
  const priorities: string[] = [];
  if (['expert', 'professor', 'mentor'].includes(archetype))
    priorities.push('My expertise', 'Opinions / hot takes');
  if (['disruptor', 'rebel', 'maverick'].includes(archetype))
    priorities.push('Opinions / hot takes', 'My journey');
  if (['storyteller', 'friend', 'neighbor'].includes(archetype))
    priorities.push('My journey', "My audience's problems");
  if (['founder', 'builder', 'creator'].includes(archetype))
    priorities.push('My journey', 'My product / service', 'My expertise');
  if (['authority', 'guide', 'sage'].includes(archetype))
    priorities.push('My expertise', "My audience's problems");
  return reorderByPriority(IDEAS_DIRECTIONS, priorities);
}

function getBrandAwareAudiencePain(brandSettings: BrandSettingsShape): string[] {
  const tone = brandSettings?.voice?.toneSpectrum;
  const audience = brandSettings?.voice?.targetAudience?.toLowerCase() ?? '';
  const priorities: string[] = [];
  if (
    audience.includes('developer') ||
    audience.includes('technical') ||
    audience.includes('engineer')
  )
    priorities.push('Technical problems', 'Advanced challenges');
  if (audience.includes('beginner') || audience.includes('new') || audience.includes('starter'))
    priorities.push('Beginner struggles', 'Mindset / motivation');
  if (
    audience.includes('founder') ||
    audience.includes('startup') ||
    audience.includes('entrepreneur')
  )
    priorities.push('Scaling / growth issues', 'Mistakes / failures');
  if (audience.includes('b2b') || audience.includes('business') || audience.includes('enterprise'))
    priorities.push('Scaling / growth issues', 'Industry insights');
  if ((tone?.empathy ?? 50) > 65) priorities.push('Mindset / motivation', 'Beginner struggles');
  return reorderByPriority(IDEAS_AUDIENCE_PAIN, priorities);
}

function getBrandAwareIdeaStyles(brandSettings: BrandSettingsShape): string[] {
  const tone = brandSettings?.voice?.toneSpectrum;
  const archetype = brandSettings?.voice?.personaArchetype ?? '';
  if (!tone && !archetype) return IDEAS_STYLES;
  const priorities: string[] = [];
  if (['expert', 'professor', 'mentor', 'guide'].includes(archetype))
    priorities.push('Educational', 'Analytical');
  if (['disruptor', 'rebel', 'maverick'].includes(archetype)) priorities.push('Opinionated');
  if (['storyteller', 'friend'].includes(archetype)) priorities.push('Storytelling', 'Personal');
  if ((tone?.boldness ?? 50) > 65) priorities.push('Opinionated');
  if ((tone?.empathy ?? 50) > 65) priorities.push('Personal', 'Storytelling');
  if ((tone?.humor ?? 50) > 65) priorities.push('Entertaining', 'Short-form punchy');
  if ((tone?.formality ?? 50) < 40) priorities.push('Personal', 'Short-form punchy');
  return reorderByPriority(IDEAS_STYLES, priorities);
}

function getBrandAwarePrimaryPlatform(brandSettings: BrandSettingsShape): string | undefined {
  const audience = brandSettings?.voice?.targetAudience?.toLowerCase() ?? '';
  const archetype = brandSettings?.voice?.personaArchetype ?? '';
  const hashtags = brandSettings?.voice?.hashtagStrategy?.toLowerCase() ?? '';

  if (
    audience.includes('developer') ||
    audience.includes('technical') ||
    audience.includes('engineer') ||
    audience.includes('b2b') ||
    audience.includes('enterprise')
  )
    return 'LinkedIn';
  if (
    audience.includes('creator') ||
    audience.includes('artist') ||
    audience.includes('visual') ||
    audience.includes('lifestyle')
  )
    return 'Instagram';
  if (['disruptor', 'rebel', 'maverick'].includes(archetype)) return 'X';
  if (['expert', 'professor', 'sage', 'authority', 'guide'].includes(archetype)) return 'LinkedIn';
  if (hashtags.includes('tiktok') || hashtags.includes('viral') || archetype === 'entertainer')
    return 'TikTok';
  return undefined;
}

function getBrandAwarePrimaryGoal(brandSettings: BrandSettingsShape): string | undefined {
  const archetype = brandSettings?.voice?.personaArchetype ?? '';
  const tone = brandSettings?.voice?.toneSpectrum;

  if (['expert', 'professor', 'sage', 'authority', 'guide'].includes(archetype)) return 'Authority';
  if (['creator', 'builder', 'founder'].includes(archetype)) return 'Growth';
  if (['disruptor', 'rebel', 'maverick'].includes(archetype)) return 'Engagement';
  if ((tone?.boldness ?? 50) > 70) return 'Engagement';
  return undefined;
}

function getBrandAwareContentTypeDistribution(
  brandSettings: BrandSettingsShape,
): Record<string, number> {
  const tone = brandSettings?.voice?.toneSpectrum;
  const archetype = brandSettings?.voice?.personaArchetype ?? '';

  if (['storyteller', 'friend'].includes(archetype))
    return {
      'Story / Narrative': 35,
      'Insight / Lessons': 15,
      'Opinion / Hot takes': 15,
      'Tactical / How-to': 10,
      'Relatable / Observational': 25,
    };
  if (['expert', 'professor', 'sage'].includes(archetype))
    return {
      'Story / Narrative': 10,
      'Insight / Lessons': 35,
      'Opinion / Hot takes': 20,
      'Tactical / How-to': 30,
      'Relatable / Observational': 5,
    };
  if (['disruptor', 'rebel', 'maverick'].includes(archetype))
    return {
      'Story / Narrative': 20,
      'Insight / Lessons': 15,
      'Opinion / Hot takes': 40,
      'Tactical / How-to': 5,
      'Relatable / Observational': 20,
    };
  if (['creator', 'builder', 'founder'].includes(archetype))
    return {
      'Story / Narrative': 30,
      'Insight / Lessons': 25,
      'Opinion / Hot takes': 10,
      'Tactical / How-to': 20,
      'Relatable / Observational': 15,
    };

  // Tone-based adjustments from the default 20/20/20/20/20 split
  let story = 20,
    insight = 20,
    opinion = 20,
    tactical = 20,
    relatable = 20;
  if ((tone?.empathy ?? 50) > 65) {
    story += 10;
    relatable += 5;
    insight -= 10;
    tactical -= 5;
  }
  if ((tone?.boldness ?? 50) > 65) {
    opinion += 15;
    story -= 5;
    tactical -= 5;
    relatable -= 5;
  }
  if ((tone?.humor ?? 50) > 65) {
    relatable += 10;
    story += 5;
    opinion -= 10;
    tactical -= 5;
  }
  if ((tone?.formality ?? 50) > 65) {
    insight += 10;
    tactical += 10;
    opinion -= 10;
    relatable -= 10;
  }

  return {
    'Story / Narrative': Math.max(0, story),
    'Insight / Lessons': Math.max(0, insight),
    'Opinion / Hot takes': Math.max(0, opinion),
    'Tactical / How-to': Math.max(0, tactical),
    'Relatable / Observational': Math.max(0, relatable),
  };
}

function getBrandAwareHookStrategyOrder(brandSettings: BrandSettingsShape): string[] {
  const archetype = brandSettings?.voice?.personaArchetype ?? '';
  const tone = brandSettings?.voice?.toneSpectrum;

  if (['expert', 'professor', 'sage', 'authority'].includes(archetype))
    return [
      'Authority / Results',
      'Specific / Relatable',
      'Curiosity / Teaser',
      'Problem-first',
      'Question-based',
      'Narrative / Story-led',
      'Contrarian / Hot take',
      'Shock / Unexpected',
    ];
  if (['disruptor', 'rebel', 'maverick'].includes(archetype))
    return [
      'Contrarian / Hot take',
      'Shock / Unexpected',
      'Problem-first',
      'Question-based',
      'Specific / Relatable',
      'Curiosity / Teaser',
      'Narrative / Story-led',
      'Authority / Results',
    ];
  if (['storyteller', 'friend'].includes(archetype))
    return [
      'Narrative / Story-led',
      'Specific / Relatable',
      'Problem-first',
      'Question-based',
      'Curiosity / Teaser',
      'Authority / Results',
      'Contrarian / Hot take',
      'Shock / Unexpected',
    ];
  if (['creator', 'builder', 'founder'].includes(archetype))
    return [
      'Curiosity / Teaser',
      'Specific / Relatable',
      'Problem-first',
      'Narrative / Story-led',
      'Authority / Results',
      'Question-based',
      'Contrarian / Hot take',
      'Shock / Unexpected',
    ];
  if ((tone?.boldness ?? 50) > 65)
    return [
      'Contrarian / Hot take',
      'Problem-first',
      'Shock / Unexpected',
      'Authority / Results',
      'Curiosity / Teaser',
      'Question-based',
      'Specific / Relatable',
      'Narrative / Story-led',
    ];

  return HOOK_STRATEGIES;
}

function getBrandAwareDefaultHookStrategy(brandSettings: BrandSettingsShape): string[] {
  // Pre-select the top 3 from the brand-aware ordering as smart defaults
  return getBrandAwareHookStrategyOrder(brandSettings).slice(0, 3);
}

function getBrandAwareInteractionStyles(brandSettings: BrandSettingsShape): string[] {
  const tone = brandSettings?.voice?.toneSpectrum;
  const archetype = brandSettings?.voice?.personaArchetype ?? '';
  const priorities: string[] = [];

  if ((tone?.boldness ?? 50) > 65)
    priorities.push('Debate-driven (opinions)', 'Passive (strong statement)');
  if ((tone?.empathy ?? 50) > 65)
    priorities.push('Conversational (comments)', 'Light (save/share/follow)');
  if (['expert', 'authority', 'sage'].includes(archetype))
    priorities.push('Passive (strong statement)', 'Authority (no CTA)');
  if (['disruptor', 'rebel', 'maverick'].includes(archetype))
    priorities.push('Debate-driven (opinions)', 'Passive (strong statement)');

  return reorderByPriority(INTERACTION_STYLES, priorities);
}

function getBrandAwareDefaultInteractionStyles(brandSettings: BrandSettingsShape): string[] {
  const styles = getBrandAwareInteractionStyles(brandSettings);
  // Pre-select top 2 as smart defaults
  return styles.slice(0, 2);
}

function getBrandAwareVariationLevel(brandSettings: BrandSettingsShape): 'low' | 'medium' | 'high' {
  const tone = brandSettings?.voice?.toneSpectrum;
  const archetype = brandSettings?.voice?.personaArchetype ?? '';

  if (['disruptor', 'rebel', 'maverick', 'creator'].includes(archetype)) return 'high';
  if (['expert', 'professor', 'sage'].includes(archetype)) return 'low';
  if ((tone?.boldness ?? 50) > 70) return 'high';
  if ((tone?.formality ?? 50) > 65) return 'low';
  return 'medium';
}

function getBrandAwarePillarIntents(brandSettings: BrandSettingsShape): Record<string, string> {
  const archetype = brandSettings?.voice?.personaArchetype ?? '';
  const tone = brandSettings?.voice?.toneSpectrum;
  const pillars =
    brandSettings?.voice?.contentPillars
      ?.split(',')
      .map((p) => p.trim())
      .filter(Boolean) ?? [];

  const intents: Record<string, string> = {};
  for (const pillar of pillars) {
    const pLower = pillar.toLowerCase();
    if (
      pLower.includes('bts') ||
      pLower.includes('behind') ||
      pLower.includes('scene') ||
      pLower.includes('journey') ||
      pLower.includes('day in')
    )
      intents[pillar] = 'Document';
    else if (
      pLower.includes('tip') ||
      pLower.includes('how') ||
      pLower.includes('guide') ||
      pLower.includes('teach') ||
      pLower.includes('educat')
    )
      intents[pillar] = 'Teach';
    else if (
      pLower.includes('opinion') ||
      pLower.includes('hot take') ||
      pLower.includes('take') ||
      pLower.includes('thought lead')
    )
      intents[pillar] = 'Provoke';
    else if (
      pLower.includes('entertain') ||
      pLower.includes('fun') ||
      pLower.includes('humor') ||
      pLower.includes('meme')
    )
      intents[pillar] = 'Entertain';
    else {
      // Fallback by archetype
      if (['expert', 'professor', 'sage'].includes(archetype)) intents[pillar] = 'Teach';
      else if (['disruptor', 'rebel', 'maverick'].includes(archetype)) intents[pillar] = 'Provoke';
      else if (['storyteller', 'friend', 'creator', 'builder', 'founder'].includes(archetype))
        intents[pillar] = 'Document';
      else intents[pillar] = (tone?.boldness ?? 50) > 60 ? 'Provoke' : 'Teach';
    }
  }
  return intents;
}

function getBrandAwareHookPreferences(brandSettings: BrandSettingsShape): string[] {
  const tone = brandSettings?.voice?.toneSpectrum;
  const archetype = brandSettings?.voice?.personaArchetype ?? '';
  if (!tone && !archetype) return IDEAS_HOOK_PREFERENCES;
  const priorities: string[] = [];
  if (['disruptor', 'rebel', 'maverick'].includes(archetype))
    priorities.push('Contrarian takes', 'Strong opinions', 'Controversial takes');
  if (['friend', 'neighbor'].includes(archetype))
    priorities.push('Relatable moments', 'Personal story hooks');
  if (['storyteller'].includes(archetype))
    priorities.push('Personal story hooks', '"I learned this the hard way"');
  if (['expert', 'professor', 'sage'].includes(archetype))
    priorities.push('Data / insight-driven', 'Direct statements');
  if ((tone?.boldness ?? 50) > 65) priorities.push('Strong opinions');
  if ((tone?.empathy ?? 50) > 65) priorities.push('Relatable moments');
  return reorderByPriority(IDEAS_HOOK_PREFERENCES, priorities);
}

function formatCalendarPlanSummary(
  plan: CalendarPlanState,
  brandSettings: BrandSettingsShape,
): string {
  const lines: string[] = [];

  const cadenceLabel =
    plan.cadenceType === 'per_week'
      ? `${plan.cadenceValue} posts/week`
      : `${plan.cadenceValue} posts/day`;

  const brandPillars = brandSettings?.voice?.contentPillars
    ? brandSettings.voice.contentPillars
        .split(',')
        .map((p) => p.trim())
        .filter(Boolean)
    : [];

  const pillarsLabel =
    plan.pillarMode === 'default'
      ? brandPillars.length > 0
        ? brandPillars.map((p) => {
            const intent = plan.pillarIntents[p];
            return intent ? `${p} (${intent})` : p;
          })
        : ['Default brand pillars']
      : plan.pillarWeights
          .filter((p) => p.weight > 0)
          .map((p) => {
            const intent = plan.pillarIntents[p.name];
            return intent ? `${p.name} ${p.weight}% — ${intent}` : `${p.name} ${p.weight}%`;
          });

  const inspirationLabel =
    plan.inspirationMode === 'use' && plan.selectedInspirationIds.length > 0
      ? `${plan.selectedInspirationIds.length} item${plan.selectedInspirationIds.length !== 1 ? 's' : ''} from feed`
      : plan.inspirationMode === 'use' && plan.inspirationSeed.trim()
        ? 'Seed ideas provided'
        : 'Generate from scratch';

  const contentTypeTotal = CONTENT_TYPE_LABELS.reduce(
    (sum, t) => sum + (plan.contentTypeDistribution[t] ?? 0),
    0,
  );

  lines.push('## Content Calendar Plan');

  lines.push('');
  lines.push('### Schedule');
  lines.push(`- **Duration:** ${plan.timeframeValue} ${plan.timeframeUnit}`);
  lines.push(`- **Cadence:** ${cadenceLabel}`);
  lines.push(`- **Primary platform:** ${plan.primaryPlatform || 'Not set'}`);
  if (plan.repurpose) lines.push(`- **Repurposing:** Enabled`);

  lines.push('');
  lines.push('### Goal');
  lines.push(`- **Primary:** ${plan.goal || 'Not set'}`);
  if (plan.secondaryGoal.trim()) lines.push(`- **Secondary:** ${plan.secondaryGoal.trim()}`);

  lines.push('');
  lines.push('### Content Pillars');
  pillarsLabel.forEach((p) => lines.push(`- ${p}`));

  if (contentTypeTotal > 0) {
    lines.push('');
    lines.push('### Content Type Mix');
    CONTENT_TYPE_LABELS.forEach((t) => {
      const w = plan.contentTypeDistribution[t] ?? 0;
      if (w > 0) lines.push(`- ${t}: ${w}%`);
    });
  }

  if (plan.hookStrategy.length > 0) {
    lines.push('');
    lines.push('### Hook Strategy (by priority)');
    plan.hookStrategy.forEach((h, i) => lines.push(`${i + 1}. ${h}`));
  }

  if (plan.interactionStyle.length > 0) {
    lines.push('');
    lines.push('### Interaction Style');
    lines.push(plan.interactionStyle.join(', '));
  }

  lines.push('');
  lines.push('### Variation Level');
  lines.push(
    `${plan.variationLevel.charAt(0).toUpperCase() + plan.variationLevel.slice(1)} — ${VARIATION_DESCRIPTIONS[plan.variationLevel].desc}`,
  );

  lines.push('');
  lines.push('### Source');
  lines.push(inspirationLabel);

  if (
    plan.constraintsInclude.length > 0 ||
    plan.constraintsAvoid.length > 0 ||
    plan.constraints.trim()
  ) {
    lines.push('');
    lines.push('### Constraints');
    if (plan.constraintsInclude.length > 0)
      lines.push(`- Include: ${plan.constraintsInclude.join(', ')}`);
    if (plan.constraintsAvoid.length > 0)
      lines.push(`- Avoid: ${plan.constraintsAvoid.join(', ')}`);
    if (plan.constraints.trim()) lines.push(`- Note: ${plan.constraints.trim()}`);
  }

  if (brandSettings?.voice?.toneSpectrum) {
    const tone = brandSettings.voice.toneSpectrum;
    lines.push('');
    lines.push('### Tone Alignment');
    lines.push(`- Formality: ${tone.formality}/100`);
    lines.push(`- Humor: ${tone.humor}/100`);
    lines.push(`- Boldness: ${tone.boldness}/100`);
    lines.push(`- Empathy: ${tone.empathy}/100`);
  }

  return lines.join('\n');
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components: Typing Indicator
// ─────────────────────────────────────────────────────────────────────────────

function TypingIndicator() {
  return (
    <div className="flex gap-1.5 items-center h-5">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="inline-block w-1.5 h-1.5 rounded-full bg-muted-foreground/50 animate-bounce"
          style={{ animationDelay: `${i * 0.15}s`, animationDuration: '1.2s' }}
        />
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components: Goal Cards
// ─────────────────────────────────────────────────────────────────────────────

interface GoalCardsProps {
  onSelect: (goal: 'ideas' | 'calendar' | 'draft') => void;
  selected?: string;
  disabled?: boolean;
}

const GOALS = [
  {
    id: 'ideas' as const,
    iconBg: '#F3F0FF',
    icon: <LightbulbIcon className="text-black" />,
    iconColor: '#534AB7',
    label: 'Content ideas',
    description: 'Generate post ideas for a specific topic, platform, or pillar',
  },
  {
    id: 'calendar' as const,
    iconBg: '#E1F5EE',
    icon: <CalendarIcon className="text-black" />,
    iconColor: '#0F6E56',
    label: 'Calendar plan',
    description: 'Plan a week or month of posts balanced across your pillars',
  },
  {
    id: 'draft' as const,
    iconBg: '#FEF3E2',
    icon: <PenToolIcon className="text-black" />,
    iconColor: '#854F0B',
    label: 'Draft from scratch',
    description: 'Write a full post in your brand voice, ready for the composer',
  },
];

function GoalCards({ onSelect, selected, disabled }: GoalCardsProps) {
  return (
    <div className={cn('flex flex-col gap-2 mt-2', disabled && 'pointer-events-none opacity-60')}>
      {GOALS.map((goal) => (
        <button
          key={goal.id}
          type="button"
          onClick={() => onSelect(goal.id)}
          disabled={disabled}
          className={cn(
            'flex items-center gap-3 rounded-[10px] border p-[12px_14px] text-left transition-all duration-150 shadow-social-card',
            selected === goal.id
              ? 'border-primary/30 bg-muted/60'
              : 'border-border hover:border-primary hover:-translate-y-px',
          )}
        >
          <span
            className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ background: goal.iconBg }}
          >
            {goal.icon}
          </span>
          <span>
            <span className="block text-[13px] font-medium text-foreground">{goal.label}</span>
            <span className="block text-[11px] text-muted-foreground mt-0.5">
              {goal.description}
            </span>
          </span>
        </button>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components: Platform Picker
// ─────────────────────────────────────────────────────────────────────────────

interface PlatformPickerProps {
  selected: string[];
  onToggle: (p: string) => void;
  onConfirm: () => void;
  isGenerating: boolean;
}

function PlatformPicker({ selected, onToggle, onConfirm, isGenerating }: PlatformPickerProps) {
  return (
    <div className="mt-2">
      <p className="text-[11px] text-muted-foreground mb-2">Select platforms (or leave general):</p>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {PLATFORMS.map((p) => {
          const active = selected.includes(p);
          return (
            <button
              key={p}
              type="button"
              onClick={() => onToggle(p)}
              disabled={isGenerating}
              className={cn(
                'rounded-full border px-[11px] py-[5px] text-[12px] transition-colors',
                active
                  ? 'bg-[#0360FE] text-primary-foreground border-primary'
                  : 'border-border text-muted-foreground hover:border-foreground/40 hover:text-foreground',
                isGenerating && 'opacity-50 pointer-events-none',
              )}
            >
              {p}
            </button>
          );
        })}
      </div>
      <Button size="sm" onClick={onConfirm} disabled={isGenerating}>
        {isGenerating ? 'Generating…' : 'Generate ideas →'}
      </Button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components: Calendar Setup Picker
// ─────────────────────────────────────────────────────────────────────────────

interface CalendarSetupPickerProps {
  timeAmount: number;
  timeUnit: 'weeks' | 'months';
  scheduleType: 'per-period' | 'daily';
  postsPerPeriod: number;
  postsPerDay: number;
  selectedPlatforms: string[];
  seedIdeas: string;
  onTimeAmountChange: (v: number) => void;
  onTimeUnitChange: (v: 'weeks' | 'months') => void;
  onScheduleTypeChange: (v: 'per-period' | 'daily') => void;
  onPostsPerPeriodChange: (v: number) => void;
  onPostsPerDayChange: (v: number) => void;
  onPlatformToggle: (p: string) => void;
  onSeedIdeasChange: (v: string) => void;
  onConfirm: () => void;
  isGenerating: boolean;
}

function CalendarSetupPicker({
  timeAmount,
  timeUnit,
  scheduleType,
  postsPerPeriod,
  postsPerDay,
  selectedPlatforms,
  seedIdeas,
  onTimeAmountChange,
  onTimeUnitChange,
  onScheduleTypeChange,
  onPostsPerPeriodChange,
  onPostsPerDayChange,
  onPlatformToggle,
  onSeedIdeasChange,
  onConfirm,
  isGenerating,
}: CalendarSetupPickerProps) {
  const periodLabel = timeUnit === 'weeks' ? 'week' : 'month';

  return (
    <div className="mt-2 space-y-4">
      {/* Time frame */}
      <div>
        <p className="text-[11px] text-muted-foreground mb-1.5">How far out?</p>
        <div className="flex items-center gap-2">
          <input
            type="number"
            min={1}
            max={52}
            value={timeAmount}
            onChange={(e) =>
              onTimeAmountChange(Math.max(1, Math.min(52, parseInt(e.target.value) || 1)))
            }
            disabled={isGenerating}
            className="resize-none w-16 text-center rounded-xl border border-border bg-background px-3 py-2 text-[12px] text-foreground placeholder:text-muted-foreground/50 outline-none focus:border-primary leading-relaxed rounded-[10px] outline-none transition-all duration-150 shadow-social-card"
          />
          <div className="flex rounded-md border border-border overflow-hidden">
            {(['weeks', 'months'] as const).map((unit) => (
              <button
                key={unit}
                type="button"
                onClick={() => onTimeUnitChange(unit)}
                disabled={isGenerating}
                className={cn(
                  'px-3 py-1.5 text-[12px] transition-colors',
                  timeUnit === unit
                    ? 'bg-[#0360FE] text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted',
                  unit === 'months' && 'border-l border-border',
                )}
              >
                {unit.charAt(0).toUpperCase() + unit.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Posting schedule */}
      <div>
        <p className="text-[11px] text-muted-foreground mb-1.5">Posting schedule</p>
        <div className="flex rounded-md border border-border overflow-hidden mb-2">
          <button
            type="button"
            onClick={() => onScheduleTypeChange('per-period')}
            disabled={isGenerating}
            className={cn(
              'flex-1 py-1.5 text-[12px] transition-colors',
              scheduleType === 'per-period'
                ? 'bg-[#0360FE] text-primary-foreground'
                : 'text-muted-foreground hover:bg-muted',
            )}
          >
            Per {periodLabel}
          </button>
          <button
            type="button"
            onClick={() => onScheduleTypeChange('daily')}
            disabled={isGenerating}
            className={cn(
              'flex-1 py-1.5 text-[12px] transition-colors border-l border-border',
              scheduleType === 'daily'
                ? 'bg-[#0360FE] text-primary-foreground'
                : 'text-muted-foreground hover:bg-muted',
            )}
          >
            Daily
          </button>
        </div>
        {scheduleType === 'per-period' ? (
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={1}
              max={30}
              value={postsPerPeriod}
              onChange={(e) =>
                onPostsPerPeriodChange(Math.max(1, Math.min(30, parseInt(e.target.value) || 1)))
              }
              disabled={isGenerating}
              className="resize-none w-16 text-center rounded-xl border border-border bg-background px-3 py-2 text-[12px] text-foreground placeholder:text-muted-foreground/50 outline-none focus:border-primary leading-relaxed rounded-[10px] outline-none transition-all duration-150 shadow-social-card"
            />
            <span className="text-[12px] text-muted-foreground">posts per {periodLabel}</span>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={1}
              max={5}
              value={postsPerDay}
              onChange={(e) =>
                onPostsPerDayChange(Math.max(1, Math.min(5, parseInt(e.target.value) || 1)))
              }
              disabled={isGenerating}
              className="w-16 rounded-md border border-border bg-background px-2 py-1.5 text-[13px] text-center text-foreground outline-none focus:border-primary"
            />
            <span className="text-[12px] text-muted-foreground">posts per day</span>
          </div>
        )}
      </div>

      {/* Platforms */}
      <div>
        <p className="text-[11px] text-muted-foreground mb-1.5">Platforms (or leave general):</p>
        <div className="flex flex-wrap gap-1.5">
          {PLATFORMS.map((p) => {
            const active = selectedPlatforms.includes(p);
            return (
              <button
                key={p}
                type="button"
                onClick={() => onPlatformToggle(p)}
                disabled={isGenerating}
                className={cn(
                  'rounded-full border px-[11px] py-[5px] text-[12px] transition-colors',
                  active
                    ? 'bg-[#0360FE] text-primary-foreground border-primary'
                    : 'border-border text-muted-foreground hover:border-foreground/40 hover:text-foreground',
                  isGenerating && 'opacity-50 pointer-events-none',
                )}
              >
                {p}
              </button>
            );
          })}
        </div>
      </div>

      {/* Seed ideas */}
      <div>
        <p className="text-[11px] text-muted-foreground mb-1.5">
          Content ideas <span className="text-muted-foreground/60">(optional — one per line)</span>
        </p>
        <textarea
          value={seedIdeas}
          onChange={(e) => onSeedIdeasChange(e.target.value)}
          disabled={isGenerating}
          placeholder="Leave blank to generate from scratch, or add ideas to seed the calendar…"
          rows={3}
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-[12px] text-foreground placeholder:text-muted-foreground/50 outline-none focus:border-primary resize-none leading-relaxed"
        />
      </div>

      <Button size="sm" onClick={onConfirm} disabled={isGenerating}>
        {isGenerating ? 'Building…' : 'Build calendar plan →'}
      </Button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components: Calendar Planning Flow
// ─────────────────────────────────────────────────────────────────────────────

interface CalendarStep1Props {
  timeframeValue: number;
  timeframeUnit: 'days' | 'weeks' | 'months';
  cadenceType: 'per_week' | 'daily';
  cadenceValue: number;
  onTimeframeValueChange: (v: number) => void;
  onTimeframeUnitChange: (u: 'days' | 'weeks' | 'months') => void;
  onCadenceTypeChange: (t: 'per_week' | 'daily') => void;
  onCadenceValueChange: (v: number) => void;
  onNext: () => void;
  disabled: boolean;
}

function CalendarStep1({
  timeframeValue,
  timeframeUnit,
  cadenceType,
  cadenceValue,
  onTimeframeValueChange,
  onTimeframeUnitChange,
  onCadenceTypeChange,
  onCadenceValueChange,
  onNext,
  disabled,
}: CalendarStep1Props) {
  const cadenceLabel = cadenceType === 'per_week' ? 'posts/week' : 'posts/day';
  return (
    <div className="mt-2 space-y-3">
      {/* Timeframe */}
      <div>
        <p className="text-[11px] text-muted-foreground mb-1.5">Timeframe</p>
        <div className="flex items-center gap-2">
          <input
            type="number"
            min={1}
            max={52}
            value={timeframeValue}
            onChange={(e) =>
              onTimeframeValueChange(Math.max(1, Math.min(52, parseInt(e.target.value) || 1)))
            }
            disabled={disabled}
            className="w-14 rounded-md border border-border bg-background px-2 py-1.5 text-[13px] text-foreground outline-none focus:border-primary text-center"
          />
          <div className="flex shadow-social-lighter overflow-hidden bg-background p-1 rounded-full gap-1 flex-1">
            {(['days', 'weeks'] as const).map((unit) => (
              <button
                key={unit}
                type="button"
                onClick={() => onTimeframeUnitChange(unit)}
                disabled={disabled}
                className={cn(
                  'px-2.5 py-1.5 text-[12px] transition-colors rounded-full shrink-0 flex-1 font-mono uppercase',
                  timeframeUnit === unit
                    ? 'bg-[#0360FE] text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted',
                )}
              >
                {unit.charAt(0).toUpperCase() + unit.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Cadence */}
      <div>
        <p className="text-[11px] text-muted-foreground mb-1.5">Posting cadence</p>
        <div className="flex items-center gap-2">
          <div className="flex shadow-social-lighter bg-background p-1 overflow-hidden gap-1 rounded-full flex-1">
            {(['per_week', 'daily'] as const).map((type) => (
              <button
                key={type}
                type="button"
                onClick={() => onCadenceTypeChange(type)}
                disabled={disabled}
                className={cn(
                  'px-3 py-1.5 text-[12px] transition-colors rounded-full shrink-0 flex-1 font-mono uppercase',
                  cadenceType === type
                    ? 'bg-[#0360FE] text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted',
                )}
              >
                {type === 'per_week' ? 'Per week' : 'Daily'}
              </button>
            ))}
          </div>
          <input
            type="number"
            min={1}
            max={cadenceType === 'per_week' ? 30 : 5}
            value={cadenceValue}
            onChange={(e) =>
              onCadenceValueChange(
                Math.max(
                  1,
                  Math.min(cadenceType === 'per_week' ? 30 : 5, parseInt(e.target.value) || 1),
                ),
              )
            }
            disabled={disabled}
            className="w-14 rounded-md border border-border bg-background px-2 py-1.5 text-[13px] text-center text-foreground outline-none focus:border-primary"
          />
          <span className="text-[12px] text-muted-foreground">{cadenceLabel}</span>
        </div>
      </div>

      <button
        type="button"
        onClick={onNext}
        disabled={disabled}
        className="rounded-full border border-primary bg-[#0360FE] text-primary-foreground px-4 py-1.5 text-[12px] font-medium hover:opacity-90 transition-opacity disabled:opacity-40"
      >
        Next →
      </button>
    </div>
  );
}

interface CalendarChipsStepProps {
  chips: string[];
  selected: string[];
  multiSelect?: boolean;
  onToggle: (chip: string) => void;
  onNext: () => void;
  nextLabel?: string;
  disabled: boolean;
  canSkip?: boolean;
}

function CalendarChipsStep({
  chips,
  selected,
  onToggle,
  onNext,
  nextLabel = 'Next →',
  disabled,
  canSkip = false,
}: CalendarChipsStepProps) {
  const hasSelection = selected.length > 0;
  return (
    <div className="mt-2">
      <div className="flex flex-wrap gap-1.5 mb-3">
        {chips.map((chip) => {
          const active = selected.includes(chip);
          return (
            <button
              key={chip}
              type="button"
              onClick={() => onToggle(chip)}
              disabled={disabled}
              className={cn(
                'rounded-full border px-[11px] py-[5px] text-[12px] transition-colors',
                active
                  ? 'bg-[#0360FE] text-primary-foreground border-primary'
                  : 'border-border text-muted-foreground hover:border-foreground/40 hover:text-foreground',
                disabled && 'opacity-50 pointer-events-none',
              )}
            >
              {chip}
            </button>
          );
        })}
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onNext}
          disabled={disabled || (!hasSelection && !canSkip)}
          className="rounded-full border border-primary bg-[#0360FE] text-primary-foreground px-4 py-1.5 text-[12px] font-medium hover:opacity-90 transition-opacity disabled:opacity-40"
        >
          {!hasSelection && canSkip ? 'Skip →' : nextLabel}
        </button>
      </div>
    </div>
  );
}

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  new: { bg: '#E0F7FA', text: '#006064' },
  saved: { bg: '#E1F5EE', text: '#085041' },
  reviewed: { bg: '#F0F4FF', text: '#2D4FCA' },
  used: { bg: '#F3E5F5', text: '#4A148C' },
  discarded: { bg: '#FDECEA', text: '#993C1D' },
};

function InspirationItemCard({
  item,
  selected,
  onToggle,
  disabled,
}: {
  item: UnifiedContentItem;
  selected: boolean;
  onToggle: () => void;
  disabled: boolean;
}) {
  const statusColor = STATUS_COLORS[item.status] ?? { bg: '#F5F5F5', text: '#555' };
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      className={cn(
        'w-full text-left rounded-[8px] border p-2.5 transition-colors',
        selected
          ? 'border-primary bg-[#0360FE]/5'
          : 'border-border hover:border-foreground/30 bg-background',
        disabled && 'opacity-50 pointer-events-none',
      )}
    >
      <div className="flex items-start gap-2">
        {/* Selection indicator */}
        <span
          className={cn(
            'mt-0.5 flex-shrink-0 w-3.5 h-3.5 rounded border flex items-center justify-center',
            selected ? 'bg-[#0360FE] border-primary' : 'border-muted-foreground/40',
          )}
        >
          {selected && <Check size={9} className="text-primary-foreground" />}
        </span>
        <div className="flex-1 min-w-0">
          {/* Header row */}
          <div className="flex items-center gap-1.5 mb-1 flex-wrap">
            <span className="text-[11px] font-medium text-foreground truncate max-w-[100px]">
              {item.author}
            </span>
            <span className="inline-flex items-center rounded-full border border-border bg-card px-1.5 py-0 text-[10px] text-muted-foreground capitalize">
              {item.platform}
            </span>
            <span
              className="inline-flex items-center rounded-full px-1.5 py-0 text-[10px] font-medium capitalize"
              style={{ background: statusColor.bg, color: statusColor.text }}
            >
              {item.status}
            </span>
          </div>
          {/* Content preview */}
          <p className="text-[11px] text-muted-foreground line-clamp-2 leading-[1.5]">
            {item.contentPreview}
          </p>
        </div>
      </div>
    </button>
  );
}

interface InspirationStepProps {
  inspirationMode: 'use' | 'skip';
  selectedIds: string[];
  seedText: string;
  onModeChange: (mode: 'use' | 'skip') => void;
  onItemToggle: (id: string) => void;
  onSeedTextChange: (v: string) => void;
  onNext: () => void;
  items: UnifiedContentItem[];
  isLoading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  disabled: boolean;
}

function InspirationStep({
  inspirationMode,
  selectedIds,
  seedText,
  onModeChange,
  onItemToggle,
  onSeedTextChange,
  onNext,
  items,
  isLoading,
  hasMore,
  onLoadMore,
  disabled,
}: InspirationStepProps) {
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || inspirationMode !== 'use') return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoading) {
          onLoadMore();
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [inspirationMode, hasMore, isLoading, onLoadMore]);

  const canAdvance = inspirationMode === 'skip' || selectedIds.length > 0;

  return (
    <div className="mt-2 space-y-2">
      {/* Mode chips */}
      <div className="flex flex-wrap gap-1.5">
        {(['use', 'skip'] as const).map((mode) => (
          <button
            key={mode}
            type="button"
            onClick={() => onModeChange(mode)}
            disabled={disabled}
            className={cn(
              'rounded-full border px-[11px] py-[5px] text-[12px] transition-colors',
              inspirationMode === mode
                ? 'bg-[#0360FE] text-primary-foreground border-primary'
                : 'border-border text-muted-foreground hover:border-foreground/40 hover:text-foreground',
            )}
          >
            {mode === 'use' ? 'Select from feed' : 'Start from scratch'}
          </button>
        ))}
      </div>

      {/* Feed list */}
      {inspirationMode === 'use' && (
        <div
          className="overflow-y-auto rounded-[10px] border border-border space-y-1.5 p-1.5 shadow-social-card"
          style={{ maxHeight: 280 }}
        >
          {items.length === 0 && !isLoading && (
            <p className="text-[12px] text-muted-foreground text-center py-6">
              No inspiration items found. Add competitor accounts in the Inspiration feed.
            </p>
          )}
          {items.map((item) => (
            <InspirationItemCard
              key={item.contentId}
              item={item}
              selected={selectedIds.includes(item.contentId)}
              onToggle={() => onItemToggle(item.contentId)}
              disabled={disabled}
            />
          ))}
          {isLoading && (
            <div className="flex justify-center py-3">
              <div className="flex gap-1">
                {[0, 1, 2].map((i) => (
                  <span
                    key={i}
                    className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40 animate-bounce"
                    style={{ animationDelay: `${i * 0.15}s`, animationDuration: '1.2s' }}
                  />
                ))}
              </div>
            </div>
          )}
          <div ref={sentinelRef} className="h-px" />
        </div>
      )}

      {/* Selection count when items selected */}
      {inspirationMode === 'use' && selectedIds.length > 0 && (
        <p className="text-[11px] text-muted-foreground">
          {selectedIds.length} item{selectedIds.length !== 1 ? 's' : ''} selected
        </p>
      )}

      {/* Optional manual seed ideas — always visible */}
      <div className="space-y-1">
        <p className="text-[11px] text-muted-foreground">
          Add manual ideas <span className="opacity-60">(optional)</span>
        </p>
        <textarea
          value={seedText}
          onChange={(e) => onSeedTextChange(e.target.value)}
          disabled={disabled}
          placeholder="e.g. a post on our new pricing, a behind-the-scenes thread…"
          rows={2}
          className="resize-none w-full rounded-xl border border-border bg-background px-3 py-2 text-[12px] text-foreground placeholder:text-muted-foreground/50 outline-none focus:border-primary leading-relaxed rounded-[10px] outline-none transition-all duration-150 shadow-social-card"
        />
      </div>

      <button
        type="button"
        onClick={onNext}
        disabled={disabled || !canAdvance}
        className="rounded-full border border-primary bg-[#0360FE] text-primary-foreground px-4 py-1.5 text-[12px] font-medium hover:opacity-90 transition-opacity disabled:opacity-40"
      >
        {inspirationMode === 'skip' ? 'Skip →' : `Use ${selectedIds.length} selected →`}
      </button>
    </div>
  );
}

interface ConstraintsInputStepProps {
  value: string;
  onChange: (v: string) => void;
  onNext: () => void;
  disabled: boolean;
}

function ConstraintsInputStep({ value, onChange, onNext, disabled }: ConstraintsInputStepProps) {
  return (
    <div className="mt-2 space-y-2">
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        placeholder="e.g. talk about AI infra, avoid crypto, focus on builders"
        rows={3}
        className="resize-none w-full rounded-xl border border-border bg-background px-3 py-2 text-[12px] text-foreground placeholder:text-muted-foreground/50 outline-none focus:border-primary leading-relaxed rounded-[10px] outline-none transition-all duration-150 shadow-social-card"
      />
      <button
        type="button"
        onClick={onNext}
        disabled={disabled}
        className="rounded-full border border-primary bg-[#0360FE] text-primary-foreground px-4 py-1.5 text-[12px] font-medium hover:opacity-90 transition-opacity disabled:opacity-40"
      >
        {value.trim() ? 'Next →' : 'Skip →'}
      </button>
    </div>
  );
}

interface CalendarConfirmSummaryProps {
  plan: CalendarPlanState;
  brandSettings: BrandSettingsShape;
  onGenerate: () => void;
  isGenerating: boolean;
}

function CalendarConfirmSummary({
  plan,
  brandSettings,
  onGenerate,
  isGenerating,
}: CalendarConfirmSummaryProps) {
  const summary = formatCalendarPlanSummary(plan, brandSettings);
  return (
    <div className="mt-2 space-y-3">
      <div className="rounded-[10px] bg-background shadow-social-card  px-3 py-3 text-[12px] text-foreground whitespace-pre-line leading-relaxed">
        {summary}
      </div>
      <button
        type="button"
        onClick={onGenerate}
        disabled={isGenerating}
        className="rounded-full border border-primary bg-[#0360FE] text-primary-foreground px-5 py-1.5 text-[12px] font-medium hover:opacity-90 transition-opacity disabled:opacity-40"
      >
        {isGenerating ? 'Building…' : 'Generate calendar →'}
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components: New Calendar Flow Steps
// ─────────────────────────────────────────────────────────────────────────────

interface PlatformPrimaryStepProps {
  primaryPlatform: string;
  repurpose: boolean;
  suggestedPlatform: string | undefined;
  onPlatformSelect: (p: string) => void;
  onRepurposeToggle: () => void;
  onNext: () => void;
  disabled: boolean;
}

function PlatformPrimaryStep({
  primaryPlatform,
  repurpose,
  suggestedPlatform,
  onPlatformSelect,
  onRepurposeToggle,
  onNext,
  disabled,
}: PlatformPrimaryStepProps) {
  return (
    <div className="mt-2 space-y-3">
      <div>
        <p className="text-[11px] text-muted-foreground mb-1.5">
          Primary platform <span className="text-[#993C1D]">*</span>
          {suggestedPlatform && (
            <span className="ml-1.5 text-[#0360FE]/70">· suggested for your audience</span>
          )}
        </p>
        <div className="flex flex-wrap gap-1.5">
          {CALENDAR_PLATFORMS_PRIMARY.map((p) => {
            const active = primaryPlatform === p;
            const isSuggested = suggestedPlatform === p && !primaryPlatform;
            return (
              <button
                key={p}
                type="button"
                onClick={() => onPlatformSelect(p)}
                disabled={disabled}
                className={cn(
                  'rounded-full border px-[11px] py-[5px] text-[12px] transition-colors',
                  active
                    ? 'bg-[#0360FE] text-primary-foreground border-primary'
                    : isSuggested
                      ? 'border-[#0360FE]/40 text-[#0360FE] bg-[#0360FE]/5 hover:bg-[#0360FE]/10'
                      : 'border-border text-muted-foreground hover:border-foreground/40 hover:text-foreground',
                  disabled && 'opacity-50 pointer-events-none',
                )}
              >
                {p}
              </button>
            );
          })}
        </div>
      </div>

      <button
        type="button"
        onClick={onRepurposeToggle}
        disabled={disabled}
        className={cn(
          'flex items-center gap-2.5 w-full rounded-[10px] border px-3 py-2.5 text-left transition-all shadow-social-card',
          repurpose ? 'border-primary/30 bg-muted/60' : 'border-border hover:border-foreground/20',
          disabled && 'opacity-50 pointer-events-none',
        )}
      >
        <span
          className={cn(
            'w-4 h-4 rounded border flex items-center justify-center flex-shrink-0',
            repurpose ? 'bg-[#0360FE] border-primary' : 'border-muted-foreground/40',
          )}
        >
          {repurpose && <Check size={9} className="text-primary-foreground" />}
        </span>
        <span>
          <span className="block text-[12px] text-foreground font-medium">
            Repurpose to other platforms
          </span>
          <span className="block text-[10px] text-muted-foreground mt-0.5">
            Allow slight format variations when adapting to secondary platforms
          </span>
        </span>
      </button>

      <button
        type="button"
        onClick={onNext}
        disabled={disabled || !primaryPlatform}
        className="rounded-full border border-primary bg-[#0360FE] text-primary-foreground px-4 py-1.5 text-[12px] font-medium hover:opacity-90 transition-opacity disabled:opacity-40"
      >
        Next →
      </button>
    </div>
  );
}

interface GoalWithSecondaryStepProps {
  primaryGoal: string;
  secondaryGoal: string;
  suggestedGoal: string | undefined;
  onPrimaryGoalSelect: (goal: string) => void;
  onSecondaryGoalChange: (v: string) => void;
  onNext: () => void;
  disabled: boolean;
}

function GoalWithSecondaryStep({
  primaryGoal,
  secondaryGoal,
  suggestedGoal,
  onPrimaryGoalSelect,
  onSecondaryGoalChange,
  onNext,
  disabled,
}: GoalWithSecondaryStepProps) {
  return (
    <div className="mt-2 space-y-3">
      <div>
        <p className="text-[11px] text-muted-foreground mb-1.5">
          Primary goal <span className="text-[#993C1D]">*</span>
          {suggestedGoal && !primaryGoal && (
            <span className="ml-1.5 text-[#0360FE]/70">· suggested for your brand</span>
          )}
        </p>
        <div className="flex flex-wrap gap-1.5">
          {CALENDAR_GOALS_PRIMARY.map((goal) => {
            const active = primaryGoal === goal;
            const isSuggested = suggestedGoal === goal && !primaryGoal;
            return (
              <button
                key={goal}
                type="button"
                onClick={() => onPrimaryGoalSelect(goal)}
                disabled={disabled}
                className={cn(
                  'rounded-full border px-[11px] py-[5px] text-[12px] transition-colors',
                  active
                    ? 'bg-[#0360FE] text-primary-foreground border-primary'
                    : isSuggested
                      ? 'border-[#0360FE]/40 text-[#0360FE] bg-[#0360FE]/5 hover:bg-[#0360FE]/10'
                      : 'border-border text-muted-foreground hover:border-foreground/40 hover:text-foreground',
                  disabled && 'opacity-50 pointer-events-none',
                )}
              >
                {goal}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <p className="text-[11px] text-muted-foreground mb-1">
          Secondary goal or campaign focus <span className="opacity-60">(optional)</span>
        </p>
        <input
          type="text"
          value={secondaryGoal}
          onChange={(e) => onSecondaryGoalChange(e.target.value)}
          disabled={disabled}
          placeholder="e.g. promote new product launch, build newsletter list…"
          className="resize-none w-full rounded-xl border border-border bg-background px-3 py-2 text-[12px] text-foreground placeholder:text-muted-foreground/50 outline-none focus:border-primary leading-relaxed rounded-[10px] transition-all duration-150 shadow-social-card"
        />
      </div>

      <button
        type="button"
        onClick={onNext}
        disabled={disabled || !primaryGoal}
        className="rounded-full border border-primary bg-[#0360FE] text-primary-foreground px-4 py-1.5 text-[12px] font-medium hover:opacity-90 transition-opacity disabled:opacity-40"
      >
        Next →
      </button>
    </div>
  );
}

interface PillarWithIntentStepProps {
  pillarMode: 'default' | 'adjust';
  pillarWeights: PillarWeight[];
  pillarIntents: Record<string, string>;
  brandPillars: string[];
  onModeChange: (mode: 'default' | 'adjust') => void;
  onWeightChange: (name: string, weight: number) => void;
  onIntentChange: (name: string, intent: string) => void;
  onNext: () => void;
  disabled: boolean;
}

function PillarWithIntentStep({
  pillarMode,
  pillarWeights,
  pillarIntents,
  brandPillars,
  onModeChange,
  onWeightChange,
  onIntentChange,
  onNext,
  disabled,
}: PillarWithIntentStepProps) {
  const hasPillars = brandPillars.length > 0;
  const displayPillars =
    pillarMode === 'adjust'
      ? pillarWeights
      : brandPillars.map((name) => ({
          name,
          weight: Math.round(100 / Math.max(brandPillars.length, 1)),
        }));

  return (
    <div className="mt-2 space-y-2">
      <div className="flex flex-wrap gap-1.5">
        <button
          type="button"
          onClick={() => onModeChange('default')}
          disabled={disabled}
          className={cn(
            'rounded-full border px-[11px] py-[5px] text-[12px] transition-colors',
            pillarMode === 'default'
              ? 'bg-[#0360FE] text-primary-foreground border-primary'
              : 'border-border text-muted-foreground hover:border-foreground/40 hover:text-foreground',
          )}
        >
          Use my pillars (recommended)
        </button>
        {hasPillars && (
          <button
            type="button"
            onClick={() => onModeChange('adjust')}
            disabled={disabled}
            className={cn(
              'rounded-full border px-[11px] py-[5px] text-[12px] transition-colors',
              pillarMode === 'adjust'
                ? 'bg-[#0360FE] text-primary-foreground border-primary'
                : 'border-border text-muted-foreground hover:border-foreground/40 hover:text-foreground',
            )}
          >
            Adjust for this plan
          </button>
        )}
      </div>

      {displayPillars.length > 0 && (
        <div className="rounded-[10px] shadow-social-card bg-background p-3 space-y-3 mt-1">
          {displayPillars.map((pw) => (
            <div key={pw.name} className="space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-[12px] text-foreground font-medium">{pw.name}</span>
                {pillarMode === 'adjust' && (
                  <span className="text-[11px] text-muted-foreground">{pw.weight}%</span>
                )}
              </div>
              {pillarMode === 'adjust' && (
                <input
                  type="range"
                  min={0}
                  max={100}
                  value={pw.weight}
                  onChange={(e) => onWeightChange(pw.name, parseInt(e.target.value))}
                  disabled={disabled}
                  className="w-full h-1.5 accent-primary cursor-pointer"
                />
              )}
              <div className="flex flex-wrap gap-1">
                {PILLAR_INTENTS.map((intent) => {
                  const active = pillarIntents[pw.name] === intent;
                  return (
                    <button
                      key={intent}
                      type="button"
                      onClick={() => onIntentChange(pw.name, active ? '' : intent)}
                      disabled={disabled}
                      className={cn(
                        'rounded-full border px-[9px] py-[3px] text-[11px] transition-colors',
                        active
                          ? 'bg-[#0360FE]/10 text-[#0360FE] border-[#0360FE]/30 font-medium'
                          : 'border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground',
                        disabled && 'opacity-50 pointer-events-none',
                      )}
                    >
                      {intent}
                    </button>
                  );
                })}
                {/* Custom intent input */}
                {pillarIntents[pw.name] && !PILLAR_INTENTS.includes(pillarIntents[pw.name]) && (
                  <span className="rounded-full border border-[#0360FE]/30 bg-[#0360FE]/10 text-[#0360FE] px-[9px] py-[3px] text-[11px] font-medium">
                    {pillarIntents[pw.name]}
                  </span>
                )}
              </div>
            </div>
          ))}
          {pillarMode === 'adjust' &&
            (() => {
              const total = pillarWeights.reduce((sum, pw) => sum + pw.weight, 0);
              const isExact = total === 100;
              const isClose = Math.abs(total - 100) <= 5;
              return (
                <div className="flex items-center justify-between pt-1.5 border-t border-border/60">
                  <span className="text-[11px] text-muted-foreground">Total</span>
                  <span
                    className="text-[12px] font-medium tabular-nums"
                    style={{ color: isExact ? '#0F6E56' : isClose ? '#854F0B' : '#993C1D' }}
                  >
                    {total}%{isExact ? ' ✓' : ' / 100%'}
                  </span>
                </div>
              );
            })()}
        </div>
      )}

      <button
        type="button"
        onClick={onNext}
        disabled={disabled}
        className="rounded-full border border-primary bg-[#0360FE] text-primary-foreground px-4 py-1.5 text-[12px] font-medium hover:opacity-90 transition-opacity disabled:opacity-40"
      >
        {pillarMode === 'adjust' ? 'Apply →' : 'Next →'}
      </button>
    </div>
  );
}

interface ContentTypeDistributionStepProps {
  distribution: Record<string, number>;
  onChange: (type: string, value: number) => void;
  onNext: () => void;
  disabled: boolean;
}

function ContentTypeDistributionStep({
  distribution,
  onChange,
  onNext,
  disabled,
}: ContentTypeDistributionStepProps) {
  const total = CONTENT_TYPE_LABELS.reduce((sum, t) => sum + (distribution[t] ?? 0), 0);
  const isExact = total === 100;
  const isClose = Math.abs(total - 100) <= 5;

  return (
    <div className="mt-2 space-y-2">
      <div className="rounded-[10px] shadow-social-card bg-background p-3 space-y-2.5">
        {CONTENT_TYPE_LABELS.map((type) => (
          <div key={type} className="space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-[12px] text-foreground">{type}</span>
              <span className="text-[11px] text-muted-foreground tabular-nums">
                {distribution[type] ?? 0}%
              </span>
            </div>
            <input
              type="range"
              min={0}
              max={100}
              value={distribution[type] ?? 0}
              onChange={(e) => onChange(type, parseInt(e.target.value))}
              disabled={disabled}
              className="w-full h-1.5 accent-primary cursor-pointer"
            />
          </div>
        ))}
        <div className="flex items-center justify-between pt-1.5 border-t border-border/60">
          <span className="text-[11px] text-muted-foreground">Total</span>
          <span
            className="text-[12px] font-medium tabular-nums"
            style={{ color: isExact ? '#0F6E56' : isClose ? '#854F0B' : '#993C1D' }}
          >
            {total}%{isExact ? ' ✓' : ' / 100%'}
          </span>
        </div>
      </div>

      <button
        type="button"
        onClick={onNext}
        disabled={disabled}
        className="rounded-full border border-primary bg-[#0360FE] text-primary-foreground px-4 py-1.5 text-[12px] font-medium hover:opacity-90 transition-opacity disabled:opacity-40"
      >
        Next →
      </button>
    </div>
  );
}

interface HookStrategyStepProps {
  selected: string[]; // ordered by priority (index 0 = highest)
  chips: string[]; // brand-ordered suggestions
  onToggle: (hook: string) => void;
  onNext: () => void;
  disabled: boolean;
}

function HookStrategyStep({ selected, chips, onToggle, onNext, disabled }: HookStrategyStepProps) {
  return (
    <div className="mt-2 space-y-2">
      <p className="text-[11px] text-muted-foreground">
        Click to select in priority order — first selected gets used most often.
      </p>
      <div className="flex flex-wrap gap-1.5">
        {chips.map((hook) => {
          const idx = selected.indexOf(hook);
          const isSelected = idx !== -1;
          return (
            <button
              key={hook}
              type="button"
              onClick={() => onToggle(hook)}
              disabled={disabled}
              className={cn(
                'flex items-center gap-1.5 rounded-full border px-[11px] py-[5px] text-[12px] transition-colors',
                isSelected
                  ? 'bg-[#0360FE] text-primary-foreground border-primary'
                  : 'border-border text-muted-foreground hover:border-foreground/40 hover:text-foreground',
                disabled && 'opacity-50 pointer-events-none',
              )}
            >
              {isSelected && (
                <span className="inline-flex items-center justify-center w-[14px] h-[14px] rounded-full bg-white/25 text-[9px] font-medium font-mono leading-none flex-shrink-0">
                  {idx + 1}
                </span>
              )}
              {hook}
            </button>
          );
        })}
      </div>

      {selected.length > 0 && (
        <p className="text-[11px] text-muted-foreground">
          Top priority:{' '}
          {selected
            .slice(0, 3)
            .map((h, i) => `${i + 1}. ${h}`)
            .join(' · ')}
          {selected.length > 3 && ` + ${selected.length - 3} more`}
        </p>
      )}

      <div className="flex gap-2">
        <button
          type="button"
          onClick={onNext}
          disabled={disabled || selected.length === 0}
          className="rounded-full border border-primary bg-[#0360FE] text-primary-foreground px-4 py-1.5 text-[12px] font-medium hover:opacity-90 transition-opacity disabled:opacity-40"
        >
          {selected.length > 0
            ? `Use ${selected.length} style${selected.length !== 1 ? 's' : ''} →`
            : 'Select at least one'}
        </button>
        {selected.length === 0 && (
          <button
            type="button"
            onClick={onNext}
            disabled={disabled}
            className="rounded-full border border-border text-muted-foreground px-4 py-1.5 text-[12px] hover:border-foreground/40 transition-colors disabled:opacity-40"
          >
            Skip →
          </button>
        )}
      </div>
    </div>
  );
}

interface InteractionStyleStepProps {
  selected: string[];
  chips: string[];
  onToggle: (style: string) => void;
  onNext: () => void;
  disabled: boolean;
}

function InteractionStyleStep({
  selected,
  chips,
  onToggle,
  onNext,
  disabled,
}: InteractionStyleStepProps) {
  return (
    <div className="mt-2 space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {chips.map((style) => {
          const active = selected.includes(style);
          return (
            <button
              key={style}
              type="button"
              onClick={() => onToggle(style)}
              disabled={disabled}
              className={cn(
                'rounded-full border px-[11px] py-[5px] text-[12px] transition-colors',
                active
                  ? 'bg-[#0360FE] text-primary-foreground border-primary'
                  : 'border-border text-muted-foreground hover:border-foreground/40 hover:text-foreground',
                disabled && 'opacity-50 pointer-events-none',
              )}
            >
              {style}
            </button>
          );
        })}
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onNext}
          disabled={disabled || selected.length === 0}
          className="rounded-full border border-primary bg-[#0360FE] text-primary-foreground px-4 py-1.5 text-[12px] font-medium hover:opacity-90 transition-opacity disabled:opacity-40"
        >
          Next →
        </button>
        {selected.length === 0 && (
          <button
            type="button"
            onClick={onNext}
            disabled={disabled}
            className="rounded-full border border-border text-muted-foreground px-4 py-1.5 text-[12px] hover:border-foreground/40 transition-colors disabled:opacity-40"
          >
            Skip →
          </button>
        )}
      </div>
    </div>
  );
}

interface VariationLevelStepProps {
  selected: 'low' | 'medium' | 'high';
  onSelect: (level: 'low' | 'medium' | 'high') => void;
  onNext: () => void;
  disabled: boolean;
}

function VariationLevelStep({ selected, onSelect, onNext, disabled }: VariationLevelStepProps) {
  const levelColors: Record<string, string> = {
    low: '#0F6E56',
    medium: '#0360FE',
    high: '#993C1D',
  };

  return (
    <div className="mt-2 space-y-2">
      <div className="flex flex-col gap-2">
        {(['low', 'medium', 'high'] as const).map((level) => {
          const v = VARIATION_DESCRIPTIONS[level];
          const active = selected === level;
          return (
            <button
              key={level}
              type="button"
              onClick={() => onSelect(level)}
              disabled={disabled}
              className={cn(
                'flex items-start gap-3 rounded-[10px] border p-[10px_12px] text-left transition-all shadow-social-card',
                active
                  ? 'border-primary/30 bg-muted/60'
                  : 'border-border hover:border-primary/30 hover:-translate-y-px',
                disabled && 'opacity-50 pointer-events-none',
              )}
            >
              <span
                className="w-2 h-2 rounded-full flex-shrink-0 mt-1.5"
                style={{ background: levelColors[level] }}
              />
              <span>
                <span className="block text-[12px] font-medium text-foreground">{v.label}</span>
                <span className="block text-[11px] text-muted-foreground mt-0.5 leading-[1.5]">
                  {v.desc}
                </span>
                <span className="block text-[10px] text-muted-foreground/60 mt-1 italic">
                  {v.hint}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      <button
        type="button"
        onClick={onNext}
        disabled={disabled}
        className="rounded-full border border-primary bg-[#0360FE] text-primary-foreground px-4 py-1.5 text-[12px] font-medium hover:opacity-90 transition-opacity disabled:opacity-40"
      >
        Next →
      </button>
    </div>
  );
}

interface ConstraintsIncludeAvoidStepProps {
  include: string[];
  avoid: string[];
  freeText: string;
  onIncludeToggle: (item: string) => void;
  onAvoidToggle: (item: string) => void;
  onFreeTextChange: (v: string) => void;
  onNext: () => void;
  disabled: boolean;
}

function ConstraintsIncludeAvoidStep({
  include,
  avoid,
  freeText,
  onIncludeToggle,
  onAvoidToggle,
  onFreeTextChange,
  onNext,
  disabled,
}: ConstraintsIncludeAvoidStepProps) {
  const hasAny = include.length > 0 || avoid.length > 0 || freeText.trim();

  return (
    <div className="mt-2 space-y-3">
      <div>
        <p className="text-[11px] font-medium text-muted-foreground mb-1.5">Include</p>
        <div className="flex flex-wrap gap-1.5">
          {CONSTRAINTS_INCLUDE_OPTIONS.map((item) => {
            const active = include.includes(item);
            return (
              <button
                key={item}
                type="button"
                onClick={() => onIncludeToggle(item)}
                disabled={disabled}
                className={cn(
                  'rounded-full border px-[11px] py-[5px] text-[12px] transition-colors',
                  active
                    ? 'bg-[#0F6E56]/10 text-[#0F6E56] border-[#0F6E56]/40 font-medium'
                    : 'border-border text-muted-foreground hover:border-foreground/40 hover:text-foreground',
                  disabled && 'opacity-50 pointer-events-none',
                )}
              >
                {item}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <p className="text-[11px] font-medium text-muted-foreground mb-1.5">Avoid</p>
        <div className="flex flex-wrap gap-1.5">
          {CONSTRAINTS_AVOID_OPTIONS.map((item) => {
            const active = avoid.includes(item);
            return (
              <button
                key={item}
                type="button"
                onClick={() => onAvoidToggle(item)}
                disabled={disabled}
                className={cn(
                  'rounded-full border px-[11px] py-[5px] text-[12px] transition-colors',
                  active
                    ? 'bg-[#993C1D]/10 text-[#993C1D] border-[#993C1D]/40 font-medium'
                    : 'border-border text-muted-foreground hover:border-foreground/40 hover:text-foreground',
                  disabled && 'opacity-50 pointer-events-none',
                )}
              >
                {item}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <p className="text-[11px] text-muted-foreground mb-1">
          Custom note <span className="opacity-60">(optional)</span>
        </p>
        <textarea
          value={freeText}
          onChange={(e) => onFreeTextChange(e.target.value)}
          disabled={disabled}
          placeholder="e.g. always end with a learning takeaway, avoid mentioning competitors…"
          rows={2}
          className="resize-none w-full rounded-xl border border-border bg-background px-3 py-2 text-[12px] text-foreground placeholder:text-muted-foreground/50 outline-none focus:border-primary leading-relaxed rounded-[10px] transition-all duration-150 shadow-social-card"
        />
      </div>

      <button
        type="button"
        onClick={onNext}
        disabled={disabled}
        className="rounded-full border border-primary bg-[#0360FE] text-primary-foreground px-4 py-1.5 text-[12px] font-medium hover:opacity-90 transition-opacity disabled:opacity-40"
      >
        {hasAny ? 'Next →' : 'Skip →'}
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components: Chat Message
// ─────────────────────────────────────────────────────────────────────────────

interface CalendarFlowProps {
  plan: CalendarPlanState;
  brandSettings: BrandSettingsShape;
  brandPillars: string[];
  suggestedPlatform: string | undefined;
  suggestedGoal: string | undefined;
  hookStrategyChips: string[];
  interactionStyleChips: string[];
  inspirationItems: UnifiedContentItem[];
  isLoadingInspiration: boolean;
  hasMoreInspiration: boolean;
  // Step 1: timeframe + cadence
  onStep1Next: () => void;
  onTimeframeValueChange: (v: number) => void;
  onTimeframeUnitChange: (u: 'days' | 'weeks' | 'months') => void;
  onCadenceTypeChange: (t: 'per_week' | 'daily') => void;
  onCadenceValueChange: (v: number) => void;
  // Step 2: platform
  onPlatformPrimarySelect: (p: string) => void;
  onRepurposeToggle: () => void;
  onPlatformPrimaryNext: () => void;
  // Step 3: goal
  onPrimaryGoalSelect: (goal: string) => void;
  onSecondaryGoalChange: (v: string) => void;
  onGoalNext: () => void;
  // Step 4: pillars
  onPillarModeChange: (mode: 'default' | 'adjust') => void;
  onPillarWeightChange: (name: string, weight: number) => void;
  onPillarIntentChange: (pillar: string, intent: string) => void;
  onPillarsNext: () => void;
  // Step 5: content type distribution
  onContentTypeChange: (type: string, value: number) => void;
  onContentTypeNext: () => void;
  // Step 6: hook strategy
  onHookStrategyToggle: (hook: string) => void;
  onHookStrategyNext: () => void;
  // Step 7: interaction style
  onInteractionStyleToggle: (style: string) => void;
  onInteractionStyleNext: () => void;
  // Step 8: variation
  onVariationSelect: (level: 'low' | 'medium' | 'high') => void;
  onVariationNext: () => void;
  // Step 9: source input
  onInspirationModeChange: (mode: 'use' | 'skip') => void;
  onInspirationItemToggle: (id: string) => void;
  onInspirationSeedTextChange: (v: string) => void;
  onInspirationNext: () => void;
  onLoadMoreInspiration: () => void;
  // Step 10: constraints
  onIncludeToggle: (item: string) => void;
  onAvoidToggle: (item: string) => void;
  onConstraintsChange: (v: string) => void;
  onConstraintsNext: () => void;
  // Generate
  onGenerate: () => void;
  isGenerating: boolean;
}

interface IdeasFlowProps {
  flow: IdeasFlowState;
  directionChips: string[];
  audiencePainChips: string[];
  styleChips: string[];
  hookChips: string[];
  inspirationItems: UnifiedContentItem[];
  isLoadingInspiration: boolean;
  hasMoreInspiration: boolean;
  onDirectionSelect: (direction: string) => void;
  onDirectionOverrideChange: (v: string) => void;
  onAudiencePainToggle: (chip: string) => void;
  onAudiencePainOverrideChange: (v: string) => void;
  onAudienceNext: () => void;
  onStyleToggle: (style: string) => void;
  onStyleNext: () => void;
  onHookToggle: (hook: string) => void;
  onHookNext: () => void;
  onInspirationModeChange: (mode: 'use' | 'skip') => void;
  onInspirationItemToggle: (id: string) => void;
  onInspirationSeedChange: (v: string) => void;
  onInspirationNext: () => void;
  onLoadMoreInspiration: () => void;
  onConstraintsChange: (v: string) => void;
  onConstraintsNext: () => void;
  onGenerate: () => void;
  isGenerating: boolean;
}

interface ChatMessageProps {
  message: ChatMessageData;
  onGoalSelect: (goal: 'ideas' | 'calendar' | 'draft') => void;
  onTopicSubmit: (topic: string) => void;
  platformPickerState: {
    selected: string[];
    onToggle: (p: string) => void;
    onConfirm: () => void;
    isGenerating: boolean;
  };
  calendarSetupState: {
    timeAmount: number;
    timeUnit: 'weeks' | 'months';
    scheduleType: 'per-period' | 'daily';
    postsPerPeriod: number;
    postsPerDay: number;
    selectedPlatforms: string[];
    seedIdeas: string;
    onTimeAmountChange: (v: number) => void;
    onTimeUnitChange: (v: 'weeks' | 'months') => void;
    onScheduleTypeChange: (v: 'per-period' | 'daily') => void;
    onPostsPerPeriodChange: (v: number) => void;
    onPostsPerDayChange: (v: number) => void;
    onPlatformToggle: (p: string) => void;
    onSeedIdeasChange: (v: string) => void;
    onConfirm: () => void;
    isGenerating: boolean;
  };
  ideasFlowProps: IdeasFlowProps;
  calendarFlowProps: CalendarFlowProps;
  onChipClick: (chip: string) => void;
  selectedGoal?: string;
  isGenerating: boolean;
}

function ChatMessage({
  message,
  onGoalSelect,
  onTopicSubmit,
  platformPickerState,
  calendarSetupState,
  ideasFlowProps,
  calendarFlowProps,
  selectedGoal,
  isGenerating,
}: ChatMessageProps) {
  if (message.role === 'user') {
    return (
      <div className="flex flex-col items-end my-3 mt-10">
        <p
          className="text-[10px] text-muted-foreground mb-1 font-mono"
          style={{ letterSpacing: '0.02em' }}
        >
          User
        </p>
        <div
          className="max-w-[85%] rounded-[12px_12px_4px_12px] px-[13px] py-[9px] text-[13px] text-white"
          style={{ background: 'var(--social-code-bg)' }}
        >
          {message.content}
        </div>
      </div>
    );
  }

  const {
    plan,
    brandSettings: calBrandSettings,
    brandPillars,
    suggestedPlatform,
    suggestedGoal,
    hookStrategyChips,
    interactionStyleChips,
    inspirationItems,
    isLoadingInspiration,
    hasMoreInspiration,
    onStep1Next,
    onTimeframeValueChange,
    onTimeframeUnitChange,
    onCadenceTypeChange,
    onCadenceValueChange,
    onPlatformPrimarySelect,
    onRepurposeToggle,
    onPlatformPrimaryNext,
    onPrimaryGoalSelect,
    onSecondaryGoalChange,
    onGoalNext,
    onPillarModeChange,
    onPillarWeightChange,
    onPillarIntentChange,
    onPillarsNext,
    onContentTypeChange,
    onContentTypeNext,
    onHookStrategyToggle,
    onHookStrategyNext,
    onInteractionStyleToggle,
    onInteractionStyleNext,
    onVariationSelect,
    onVariationNext,
    onInspirationModeChange,
    onInspirationItemToggle,
    onInspirationSeedTextChange,
    onInspirationNext,
    onLoadMoreInspiration,
    onIncludeToggle,
    onAvoidToggle,
    onConstraintsChange,
    onConstraintsNext,
    onGenerate,
  } = calendarFlowProps;

  return (
    <div className="w-full">
      <p
        className="text-[10px] text-muted-foreground mb-1 font-mono"
        style={{ letterSpacing: '0.02em' }}
      >
        Fias AI
      </p>
      <div className="rounded-[4px_12px_12px_12px] border border-border bg-background px-[14px] py-[11px] text-[13px] leading-relaxed text-foreground w-full shadow-social-card ">
        {message.isTyping ? <TypingIndicator /> : message.content}
      </div>
      {message.element === 'goal-cards' && (
        <GoalCards onSelect={onGoalSelect} selected={selectedGoal} disabled={isGenerating} />
      )}
      {/* Ideas flow elements */}
      {message.element === 'ideas-chips-direction' && (
        <IdeasDirectionStep
          chips={ideasFlowProps.directionChips}
          selected={ideasFlowProps.flow.direction}
          override={ideasFlowProps.flow.directionOverride}
          onSelect={ideasFlowProps.onDirectionSelect}
          onOverrideChange={ideasFlowProps.onDirectionOverrideChange}
          disabled={isGenerating}
        />
      )}
      {message.element === 'ideas-chips-audience' && (
        <IdeasAudienceStep
          chips={ideasFlowProps.audiencePainChips}
          selected={ideasFlowProps.flow.audiencePain}
          override={ideasFlowProps.flow.audiencePainOverride}
          onToggle={ideasFlowProps.onAudiencePainToggle}
          onOverrideChange={ideasFlowProps.onAudiencePainOverrideChange}
          onNext={ideasFlowProps.onAudienceNext}
          disabled={isGenerating}
        />
      )}
      {message.element === 'ideas-chips-style' && (
        <CalendarChipsStep
          chips={ideasFlowProps.styleChips}
          selected={ideasFlowProps.flow.style}
          multiSelect
          onToggle={ideasFlowProps.onStyleToggle}
          onNext={ideasFlowProps.onStyleNext}
          nextLabel="Next →"
          disabled={isGenerating}
          canSkip
        />
      )}
      {message.element === 'ideas-chips-hook' && (
        <CalendarChipsStep
          chips={ideasFlowProps.hookChips}
          selected={ideasFlowProps.flow.hookPreference}
          multiSelect
          onToggle={ideasFlowProps.onHookToggle}
          onNext={ideasFlowProps.onHookNext}
          nextLabel="Next →"
          disabled={isGenerating}
          canSkip
        />
      )}
      {message.element === 'ideas-chips-inspiration' && (
        <InspirationStep
          inspirationMode={ideasFlowProps.flow.inspirationMode}
          selectedIds={ideasFlowProps.flow.selectedInspirationIds}
          seedText={ideasFlowProps.flow.inspirationSeed}
          onModeChange={ideasFlowProps.onInspirationModeChange}
          onItemToggle={ideasFlowProps.onInspirationItemToggle}
          onSeedTextChange={ideasFlowProps.onInspirationSeedChange}
          onNext={ideasFlowProps.onInspirationNext}
          items={ideasFlowProps.inspirationItems}
          isLoading={ideasFlowProps.isLoadingInspiration}
          hasMore={ideasFlowProps.hasMoreInspiration}
          onLoadMore={ideasFlowProps.onLoadMoreInspiration}
          disabled={isGenerating}
        />
      )}
      {message.element === 'ideas-constraints-input' && (
        <ConstraintsInputStep
          value={ideasFlowProps.flow.constraints}
          onChange={ideasFlowProps.onConstraintsChange}
          onNext={ideasFlowProps.onConstraintsNext}
          disabled={isGenerating}
        />
      )}
      {message.element === 'ideas-confirm' && (
        <IdeasConfirmSummary
          flow={ideasFlowProps.flow}
          onGenerate={ideasFlowProps.onGenerate}
          isGenerating={isGenerating}
        />
      )}
      {message.element === 'topic-input' && (
        <TopicInputStep onSubmit={onTopicSubmit} disabled={isGenerating} />
      )}
      {message.element === 'platform-picker' && <PlatformPicker {...platformPickerState} />}
      {message.element === 'calendar-setup' && <CalendarSetupPicker {...calendarSetupState} />}
      {message.element === 'calendar-step-1' && (
        <CalendarStep1
          timeframeValue={plan.timeframeValue}
          timeframeUnit={plan.timeframeUnit}
          cadenceType={plan.cadenceType}
          cadenceValue={plan.cadenceValue}
          onTimeframeValueChange={onTimeframeValueChange}
          onTimeframeUnitChange={onTimeframeUnitChange}
          onCadenceTypeChange={onCadenceTypeChange}
          onCadenceValueChange={onCadenceValueChange}
          onNext={onStep1Next}
          disabled={isGenerating}
        />
      )}
      {/* Calendar flow — new refactored steps */}
      {message.element === 'calendar-platform-primary' && (
        <PlatformPrimaryStep
          primaryPlatform={plan.primaryPlatform}
          repurpose={plan.repurpose}
          suggestedPlatform={suggestedPlatform}
          onPlatformSelect={onPlatformPrimarySelect}
          onRepurposeToggle={onRepurposeToggle}
          onNext={onPlatformPrimaryNext}
          disabled={isGenerating}
        />
      )}
      {message.element === 'calendar-goal-secondary' && (
        <GoalWithSecondaryStep
          primaryGoal={plan.goal}
          secondaryGoal={plan.secondaryGoal}
          suggestedGoal={suggestedGoal}
          onPrimaryGoalSelect={onPrimaryGoalSelect}
          onSecondaryGoalChange={onSecondaryGoalChange}
          onNext={onGoalNext}
          disabled={isGenerating}
        />
      )}
      {message.element === 'calendar-chips-pillars' && (
        <PillarWithIntentStep
          pillarMode={plan.pillarMode}
          pillarWeights={plan.pillarWeights}
          pillarIntents={plan.pillarIntents}
          brandPillars={brandPillars}
          onModeChange={onPillarModeChange}
          onWeightChange={onPillarWeightChange}
          onIntentChange={onPillarIntentChange}
          onNext={onPillarsNext}
          disabled={isGenerating}
        />
      )}
      {message.element === 'calendar-content-type' && (
        <ContentTypeDistributionStep
          distribution={plan.contentTypeDistribution}
          onChange={onContentTypeChange}
          onNext={onContentTypeNext}
          disabled={isGenerating}
        />
      )}
      {message.element === 'calendar-hook-strategy' && (
        <HookStrategyStep
          selected={plan.hookStrategy}
          chips={hookStrategyChips}
          onToggle={onHookStrategyToggle}
          onNext={onHookStrategyNext}
          disabled={isGenerating}
        />
      )}
      {message.element === 'calendar-interaction-style' && (
        <InteractionStyleStep
          selected={plan.interactionStyle}
          chips={interactionStyleChips}
          onToggle={onInteractionStyleToggle}
          onNext={onInteractionStyleNext}
          disabled={isGenerating}
        />
      )}
      {message.element === 'calendar-variation' && (
        <VariationLevelStep
          selected={plan.variationLevel}
          onSelect={onVariationSelect}
          onNext={onVariationNext}
          disabled={isGenerating}
        />
      )}
      {message.element === 'calendar-chips-inspiration' && (
        <InspirationStep
          inspirationMode={plan.inspirationMode}
          selectedIds={plan.selectedInspirationIds}
          seedText={plan.inspirationSeed}
          onModeChange={onInspirationModeChange}
          onItemToggle={onInspirationItemToggle}
          onSeedTextChange={onInspirationSeedTextChange}
          onNext={onInspirationNext}
          items={inspirationItems}
          isLoading={isLoadingInspiration}
          hasMore={hasMoreInspiration}
          onLoadMore={onLoadMoreInspiration}
          disabled={isGenerating}
        />
      )}
      {message.element === 'calendar-constraints-ia' && (
        <ConstraintsIncludeAvoidStep
          include={plan.constraintsInclude}
          avoid={plan.constraintsAvoid}
          freeText={plan.constraints}
          onIncludeToggle={onIncludeToggle}
          onAvoidToggle={onAvoidToggle}
          onFreeTextChange={onConstraintsChange}
          onNext={onConstraintsNext}
          disabled={isGenerating}
        />
      )}
      {message.element === 'calendar-confirm' && (
        <CalendarConfirmSummary
          plan={plan}
          brandSettings={calBrandSettings}
          onGenerate={onGenerate}
          isGenerating={isGenerating}
        />
      )}
      {/* {(message.element === 'follow-up-chips-ideas' ||
        message.element === 'follow-up-chips-calendar') && (
          <FollowUpChips
            chips={message.element === 'follow-up-chips-ideas' ? IDEAS_FOLLOW_UP : CALENDAR_FOLLOW_UP}
            onChipClick={onChipClick}
            disabled={isGenerating}
          />
        )} */}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components: Brand Context Pill
// ─────────────────────────────────────────────────────────────────────────────

interface BrandContextPillProps {
  brandName: string;
  pillarCount: number;
  onClick: () => void;
}

function BrandContextPill({ brandName, pillarCount, onClick }: BrandContextPillProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-1.5 rounded-full px-[10px] py-[4px] text-[11px] font-medium cursor-pointer transition-opacity hover:opacity-80"
      style={{ background: '#E1F5EE', color: '#085041' }}
    >
      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: '#0F6E56' }} />
      {brandName} · {pillarCount} pillars
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components: Brand Voice Modal
// ─────────────────────────────────────────────────────────────────────────────

interface BrandVoiceModalProps {
  brandSettings: BrandSettingsShape;
  onClose: () => void;
}

function BrandVoiceModal({ brandSettings, onClose }: BrandVoiceModalProps) {
  const [copied, setCopied] = useState(false);

  if (!brandSettings) return null;

  const markdownStr = buildBrandContextMarkdown(brandSettings);

  function handleCopy() {
    navigator.clipboard.writeText(markdownStr).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.5)' }}
      onClick={onClose}
    >
      <div
        className="relative bg-card rounded-[16px] border border-border shadow-xl w-full max-w-[560px] max-h-[80vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-5 pb-4 border-b border-border flex-shrink-0">
          <div>
            <p className="text-[13px] font-medium text-foreground">AI context preview</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              This is what gets injected into every AI prompt in the Writer&apos;s Table
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 ml-4">
            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={handleCopy}>
              <Copy className="h-3 w-3 mr-1" />
              {copied ? 'Copied!' : 'Copy context'}
            </Button>
            <button
              type="button"
              onClick={onClose}
              className="w-7 h-7 rounded-md flex items-center justify-center text-muted-foreground hover:bg-muted transition-colors"
            >
              <X size={14} />
            </button>
          </div>
        </div>

        {/* Code block */}
        <div className="flex-1 overflow-y-auto p-5">
          <div
            className="rounded-[9px] px-[18px] py-4 text-[12px] overflow-x-auto"
            style={{
              background: 'var(--social-code-bg)',
              fontFamily: 'monospace',
              lineHeight: '1.75',
            }}
          >
            <div style={{ color: '#5F5E5A', marginBottom: '8px' }}>
              {'// brand_context.md — injected into AI system prompt'}
            </div>
            <div
              dangerouslySetInnerHTML={{ __html: highlightMarkdown(markdownStr) }}
              style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components: Ideas Flow Steps
// ─────────────────────────────────────────────────────────────────────────────

interface IdeasDirectionStepProps {
  chips: string[];
  selected: string;
  override: string;
  onSelect: (direction: string) => void;
  onOverrideChange: (v: string) => void;
  disabled: boolean;
}

function IdeasDirectionStep({
  chips,
  selected,
  override,
  onSelect,
  onOverrideChange,
  disabled,
}: IdeasDirectionStepProps) {
  return (
    <div className="mt-2 space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {chips.map((chip) => {
          const active = selected === chip;
          return (
            <button
              key={chip}
              type="button"
              onClick={() => !disabled && onSelect(chip)}
              disabled={disabled}
              className={cn(
                'rounded-full border px-[11px] py-[5px] text-[12px] transition-colors',
                active
                  ? 'bg-[#0360FE] text-primary-foreground border-primary'
                  : 'border-border text-muted-foreground hover:border-foreground/40 hover:text-foreground',
                disabled && 'opacity-50 pointer-events-none',
              )}
            >
              {chip}
            </button>
          );
        })}
      </div>
      <input
        type="text"
        value={override}
        onChange={(e) => onOverrideChange(e.target.value)}
        disabled={disabled}
        placeholder="Or describe what you want ideas about…"
        onKeyDown={(e) => {
          if (e.key === 'Enter' && override.trim() && !disabled) {
            onSelect(override.trim());
          }
        }}
        className="resize-none w-full rounded-xl border border-border bg-background px-3 py-2 text-[12px] text-foreground placeholder:text-muted-foreground/50 outline-none focus:border-primary leading-relaxed rounded-[10px] outline-none transition-all duration-150 shadow-social-card"
      />
      {override.trim() && !selected && (
        <button
          type="button"
          onClick={() => !disabled && onSelect(override.trim())}
          disabled={disabled}
          className="rounded-full border border-primary bg-[#0360FE] text-primary-foreground px-4 py-1.5 text-[12px] font-medium hover:opacity-90 transition-opacity disabled:opacity-40"
        >
          Next →
        </button>
      )}
    </div>
  );
}

interface IdeasAudienceStepProps {
  chips: string[];
  selected: string[];
  override: string;
  onToggle: (chip: string) => void;
  onOverrideChange: (v: string) => void;
  onNext: () => void;
  disabled: boolean;
}

function IdeasAudienceStep({
  chips,
  selected,
  override,
  onToggle,
  onOverrideChange,
  onNext,
  disabled,
}: IdeasAudienceStepProps) {
  const canAdvance = selected.length > 0 || override.trim().length > 0;
  return (
    <div className="mt-2 space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {chips.map((chip) => {
          const active = selected.includes(chip);
          return (
            <button
              key={chip}
              type="button"
              onClick={() => onToggle(chip)}
              disabled={disabled}
              className={cn(
                'rounded-full border px-[11px] py-[5px] text-[12px] transition-colors',
                active
                  ? 'bg-[#0360FE] text-primary-foreground border-primary'
                  : 'border-border text-muted-foreground hover:border-foreground/40 hover:text-foreground',
                disabled && 'opacity-50 pointer-events-none',
              )}
            >
              {chip}
            </button>
          );
        })}
      </div>
      <input
        type="text"
        value={override}
        onChange={(e) => onOverrideChange(e.target.value)}
        disabled={disabled}
        placeholder="Describe your audience or specific pain points…"
        className="w-full rounded-xl border border-border bg-background px-3 py-2 text-[10px] text-foreground placeholder:text-muted-foreground/50 outline-none focus:border-primary leading-relaxed text-[14px] rounded-[10px] outline-none transition-all duration-150 shadow-social-card"
      />
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onNext}
          disabled={disabled || !canAdvance}
          className="rounded-full border border-primary bg-[#0360FE] text-primary-foreground px-4 py-1.5 text-[12px] font-medium hover:opacity-90 transition-opacity disabled:opacity-40"
        >
          Next →
        </button>
        {!canAdvance && (
          <button
            type="button"
            onClick={onNext}
            disabled={disabled}
            className="rounded-full border border-border text-muted-foreground px-4 py-1.5 text-[12px] hover:border-foreground/40 transition-colors disabled:opacity-40"
          >
            Skip →
          </button>
        )}
      </div>
    </div>
  );
}

interface IdeasConfirmSummaryProps {
  flow: IdeasFlowState;
  onGenerate: () => void;
  isGenerating: boolean;
}

function IdeasConfirmSummary({ flow, onGenerate, isGenerating }: IdeasConfirmSummaryProps) {
  const lines: string[] = [
    '## Ideas Brief',
    '',
    `Direction: ${flow.direction}${flow.directionOverride ? ` — ${flow.directionOverride}` : ''}`,
    '',
    `Audience:`,
    `${flow.audiencePain.length > 0 ? flow.audiencePain.join(', \n') : 'General'}${flow.audiencePainOverride ? ` — ${flow.audiencePainOverride}` : ''}`,
    '',
    `Style: ${flow.style.length > 0 ? flow.style.join(', ') : 'Any'}`,
    '',
    `Hooks:`,
    `${flow.hookPreference.length > 0 ? flow.hookPreference.join(', \n') : 'Any'}`,
    '',
    `Inspiration: ${flow.inspirationMode === 'skip' ? 'None' : flow.selectedInspirationIds.length > 0 ? `${flow.selectedInspirationIds.length} item${flow.selectedInspirationIds.length !== 1 ? 's' : ''} from feed` : flow.inspirationSeed.trim() ? 'Seed text provided' : 'None'}`,
  ];
  if (flow.constraints.trim()) {
    lines.push(`Constraints: ${flow.constraints.trim()}`);
  }
  const summary = lines.join('\n');

  return (
    <div className="mt-2 space-y-3">
      <div className="rounded-[10px] bg-background  px-3 py-3 text-[12px] text-foreground whitespace-pre-line leading-relaxed">
        {summary}
      </div>
      <button
        type="button"
        onClick={onGenerate}
        disabled={isGenerating}
        className="rounded-full border border-primary bg-[#0360FE] text-primary-foreground px-5 py-1.5 text-[12px] font-medium hover:opacity-90 transition-opacity disabled:opacity-40"
      >
        {isGenerating ? 'Generating…' : 'Generate 5 ideas →'}
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components: Auto-resize Textarea (legacy, no longer main entry point)
// ─────────────────────────────────────────────────────────────────────────────

function TopicInputStep({
  onSubmit,
  disabled,
}: {
  onSubmit: (topic: string) => void;
  disabled: boolean;
}) {
  const [value, setValue] = useState('');
  const canSubmit = value.trim().length > 0;
  return (
    <div className="mt-2 space-y-2">
      <input
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && canSubmit && !disabled) {
            onSubmit(value.trim());
            setValue('');
          }
        }}
        placeholder="e.g. AI for small businesses, morning routines…"
        disabled={disabled}
        autoFocus
        className="w-full rounded-xl border border-border bg-background px-3 py-2 text-[10px] text-foreground placeholder:text-muted-foreground/50 outline-none focus:border-primary leading-relaxed text-[14px] rounded-[10px] outline-none transition-all duration-150 shadow-social-card"
      />
      <button
        type="button"
        onClick={() => {
          if (canSubmit && !disabled) {
            onSubmit(value.trim());
            setValue('');
          }
        }}
        disabled={disabled || !canSubmit}
        className="rounded-full border border-primary bg-[#0360FE] text-primary-foreground px-4 py-1.5 text-[12px] font-medium hover:opacity-90 transition-opacity disabled:opacity-40"
      >
        Next →
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components: Pillar Badge
// ─────────────────────────────────────────────────────────────────────────────

function PillarBadge({ pillar }: { pillar: string }) {
  const color = getPillarColor(pillar);
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium font-mono"
      style={{ background: color.bg, color: color.text }}
    >
      {pillar}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components: Idea Card
// ─────────────────────────────────────────────────────────────────────────────

interface IdeaCardProps {
  card: IdeaCardData;
  animationDelay: number;
  onRegenerate: (id: string) => void;
  onDelete: (id: string) => void;
  onSaveDraft: (id: string) => void;
  onOpenComposer: (id: string) => void;
}

const FORMAT_LABELS: Record<string, string> = {
  tweet: 'Tweet',
  thread: 'Thread',
  carousel: 'Carousel',
  video: 'Video',
};

function IdeaCard({
  card,
  animationDelay,
  onRegenerate,
  onDelete,
  onSaveDraft,
  onOpenComposer,
}: IdeaCardProps) {
  const { idea } = card;

  return (
    <div
      className={cn(
        'rounded-[12px] border border-border overflow-hidden transition-shadow hover:shadow-none bg-background shadow-[var(--shadow-soft-blue)]',
        card.isRegenerating && 'opacity-50 pointer-events-none',
      )}
      style={{
        animation: `writerFadeUp 0.3s ease forwards`,
        animationDelay: `${animationDelay}s`,
        opacity: 0,
      }}
    >
      {/* Card header */}
      <div className="flex items-center justify-between px-[14px] py-[12px] pb-[10px]">
        <div className="flex items-center gap-1.5 flex-wrap">
          <PillarBadge pillar={idea.pillar} />
          <span className="inline-flex items-center rounded-full border border-border bg-card px-2 py-0.5 text-[10px] text-muted-foreground capitalize font-mono">
            {FORMAT_LABELS[idea.format] ?? idea.format}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => onRegenerate(card.id)}
            className="w-7 h-7 rounded-md flex items-center justify-center text-muted-foreground hover:bg-muted transition-colors"
            title="Regenerate this idea"
          >
            <RefreshCcw size={13} />
          </button>
        </div>
      </div>

      {/* Card body: hook + concept + angle */}
      <div className="px-[14px] py-[12px] border-t  border-dashed border-border space-y-1.5">
        <p className="text-[13px] font-medium leading-snug text-foreground font-satoshi">
          {idea.hook}
        </p>
        <p className="text-[12px] text-muted-foreground leading-[1.65] font-satoshi">
          {idea.concept}
        </p>
        {idea.angle && (
          <>
            <p
              className="text-[10px] text-muted-foreground uppercase font-medium mb-0.5 font-mono mt-2"
              style={{ letterSpacing: '0.06em' }}
            >
              Angle
            </p>
            <p className="text-[11px] text-muted-foreground/70 italic leading-[1.6] font-satoshi">
              {idea.angle}
            </p>
          </>
        )}
      </div>

      {/* Why it works */}
      <div className="px-[14px] py-[8px] border-t border-border ">
        <p
          className="text-[10px] text-muted-foreground uppercase font-medium mb-0.5 font-mono"
          style={{ letterSpacing: '0.06em' }}
        >
          Why it works
        </p>
        <p className="text-[11px] text-muted-foreground leading-relaxed font-satoshi">
          {idea.whyItWorks}
        </p>

        {idea.notes ? (
          <>
            <p
              className="text-[10px] text-muted-foreground uppercase font-medium mb-0.5 font-mono mt-5"
              style={{ letterSpacing: '0.06em' }}
            >
              Notes
            </p>
            <p className="text-[11px] text-muted-foreground italic flex-1 mr-2">{idea.notes}</p>
          </>
        ) : (
          <span />
        )}
      </div>

      {/* Card footer */}
      <div className="flex items-center justify-between px-[14px] py-[8px] pb-[10px] bg-muted/40 border-t border-border">
        <span />
        <div className="flex items-center gap-1.5 flex-shrink-0 font-mono">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-[11px]"
            onClick={() => onDelete(card.id)}
          >
            Delete
          </Button>
          <Button
            variant={card.saved ? 'default' : 'outline'}
            size="sm"
            className={cn(
              'h-7 px-2 text-[11px] gap-1 font-mono',
              card.saved && 'pointer-events-none',
            )}
            onClick={() => onSaveDraft(card.id)}
          >
            {card.saved ? (
              <>
                <Check size={10} /> Saved
              </>
            ) : (
              <>
                <Check size={10} /> Save draft
              </>
            )}
          </Button>
          <Button
            size="sm"
            className="h-7 px-2 text-[11px] font-mono"
            onClick={() => onOpenComposer(card.id)}
          >
            Open in composer →
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components: Calendar Post Card
// ─────────────────────────────────────────────────────────────────────────────

interface CalendarPostCardProps {
  post: CalendarPostData;
  onRegenerate: (id: string) => void;
  onSaveDraft: (id: string) => void;
  onOpenComposer: (id: string) => void;
}

function CalendarPostCard({ post, onSaveDraft, onOpenComposer }: CalendarPostCardProps) {
  return (
    <div className="rounded-[12px] border border-border bg-background overflow-hidden transition-shadow hover:shadow-none shadow-[var(--shadow-soft-blue)]">
      {/* Card header */}
      <div className="flex items-center justify-between px-[14px] py-[12px] pb-[10px]">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="inline-flex items-center rounded-full border border-border bg-card px-2 py-0.5 text-[10px] text-muted-foreground font-mono">
            {post.day}
          </span>
          <span className="inline-flex items-center rounded-full border border-border bg-card px-2 py-0.5 text-[10px] text-muted-foreground font-mono">
            {post.time}
          </span>
          <PillarBadge pillar={post.pillar} />
          <span className="inline-flex items-center rounded-full border border-border bg-card px-2 py-0.5 text-[10px] text-muted-foreground font-mono">
            {post.format}
          </span>
        </div>
      </div>

      {/* Card body */}
      <div className="px-[14px] py-[12px] border-t border-border">
        <p className="text-[13px] font-medium leading-snug text-foreground mb-2">{post.hook}</p>
        <p className="text-[12px] text-muted-foreground leading-[1.65]">{post.copy}</p>
      </div>

      {/* Card footer */}
      <div className="flex items-center justify-end gap-1.5 px-[14px] py-[8px] pb-[10px] bg-muted/40 border-t border-border">
        <Button
          variant={post.saved ? 'default' : 'outline'}
          size="sm"
          className={cn(
            'h-7 px-2 text-[11px] gap-1 font-mono',
            post.saved && 'pointer-events-none',
          )}
          onClick={() => onSaveDraft(post.id)}
        >
          {post.saved ? (
            <>
              <Check size={10} /> Saved
            </>
          ) : (
            <>
              <Check size={10} /> Save draft
            </>
          )}
        </Button>
        <Button
          size="sm"
          className="h-7 px-2 text-[11px] font-mono"
          onClick={() => onOpenComposer(post.id)}
        >
          Open in composer →
        </Button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components: Pillar Donut Chart
// ─────────────────────────────────────────────────────────────────────────────

interface PillarDonutChartProps {
  posts: CalendarPostData[];
}

function PillarDonutChart({ posts }: PillarDonutChartProps) {
  const total = posts.length;
  if (total === 0) return null;

  const counts: Record<string, number> = {};
  for (const post of posts) {
    counts[post.pillar] = (counts[post.pillar] ?? 0) + 1;
  }

  const pillars = Object.entries(counts)
    .map(([pillar, count]) => ({
      pillar,
      count,
      ratio: count / total,
      pct: Math.round((count / total) * 100),
      color: getPillarColor(pillar).hex,
    }))
    .sort((a, b) => b.count - a.count);

  const r = 36;
  const cx = 45;
  const cy = 45;
  const circumference = 2 * Math.PI * r;
  let cumulativeRatio = 0;

  return (
    <div className="rounded-[12px] shadow-social-card  p-[16px_18px] mb-4 flex gap-5 bg-background">
      {/* Donut */}
      <div className="relative flex-shrink-0" style={{ width: 90, height: 90 }}>
        <svg width={90} height={90} style={{ transform: 'rotate(-90deg)' }} viewBox="0 0 90 90">
          {/* Background track */}
          <circle
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke="currentColor"
            strokeWidth={9}
            className="text-muted/60"
          />
          {pillars.map((p) => {
            const dash = p.ratio * circumference;
            const offset = circumference * (1 - cumulativeRatio);
            cumulativeRatio += p.ratio;
            return (
              <circle
                key={p.pillar}
                cx={cx}
                cy={cy}
                r={r}
                fill="none"
                stroke={p.color}
                strokeWidth={9}
                strokeDasharray={`${dash} ${circumference}`}
                strokeDashoffset={offset}
              />
            );
          })}
        </svg>
        <div
          className="absolute inset-0 flex flex-col items-center justify-center"
          style={{ pointerEvents: 'none' }}
        >
          <span className="text-[16px] font-medium text-foreground leading-none font-satoshi">
            {total}
          </span>
          <span className="text-[9px] text-muted-foreground mt-0.5 font-mono">posts</span>
        </div>
      </div>

      {/* Legend */}
      <div className="flex-1 flex flex-col gap-[7px]">
        {pillars.map((p) => (
          <div key={p.pillar} className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: p.color }} />
            <span className="text-[12px] text-foreground w-32 truncate font-satoshi">
              {p.pillar}
            </span>
            <div className="flex-1 h-1 rounded-sm overflow-hidden bg-muted">
              <div
                className="h-full rounded-sm"
                style={{ width: `${p.ratio * 100}%`, background: p.color }}
              />
            </div>
            <span className="text-[11px] text-muted-foreground w-7 text-right font-mono">
              {p.pct}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Component: WriterTablePage
// ─────────────────────────────────────────────────────────────────────────────

export function WriterTablePage() {
  const navigate = useNavigate();
  const { selectedHolderId } = useHolderSwitcher();
  const { settings: brandSettings } = useBrandSettings(selectedHolderId);

  // ── Chat state ────────────────────────────────────────────────────────────
  const [flowStep, setFlowStep] = useState<FlowStep>('start');
  const [messages, setMessages] = useState<ChatMessageData[]>([]);
  const [selectedGoal, setSelectedGoal] = useState<string | undefined>();

  // Platform picker state (legacy — kept for CalendarSetupPicker compat)
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);

  // Ideas flow state (7-step flow)
  const [ideasFlow, setIdeasFlow] = useState<IdeasFlowState>({
    direction: '',
    directionOverride: '',
    audiencePain: [],
    audiencePainOverride: '',
    style: [],
    hookPreference: [],
    inspirationMode: 'skip',
    inspirationSeed: '',
    selectedInspirationIds: [],
    constraints: '',
  });

  // Legacy calendar setup state (kept for backward compat with CalendarSetupPicker)
  const [calendarTimeAmount, setCalendarTimeAmount] = useState(1);
  const [calendarTimeUnit, setCalendarTimeUnit] = useState<'weeks' | 'months'>('weeks');
  const [calendarScheduleType, setCalendarScheduleType] = useState<'per-period' | 'daily'>(
    'per-period',
  );
  const [calendarPostsPerPeriod, setCalendarPostsPerPeriod] = useState(5);
  const [calendarPostsPerDay, setCalendarPostsPerDay] = useState(1);
  const [calendarSeedIdeas, setCalendarSeedIdeas] = useState('');

  // New multi-step calendar planning state
  const [calendarPlan, setCalendarPlan] = useState<CalendarPlanState>({
    timeframeValue: 1,
    timeframeUnit: 'weeks',
    cadenceType: 'per_week',
    cadenceValue: 5,
    primaryPlatform: '',
    repurpose: false,
    platforms: [],
    goal: '',
    secondaryGoal: '',
    pillarMode: 'default',
    pillarWeights: [],
    pillarIntents: {},
    contentTypeDistribution: { ...DEFAULT_CONTENT_TYPE_DISTRIBUTION },
    hookStrategy: [],
    interactionStyle: [],
    variationLevel: 'medium',
    inspirationMode: 'skip',
    inspirationSeed: '',
    selectedInspirationIds: [],
    constraintsInclude: [],
    constraintsAvoid: [],
    constraints: '',
    contentStyle: [],
    engagementStyle: [],
  });

  // Inspiration feed state
  const [inspirationItems, setInspirationItems] = useState<UnifiedContentItem[]>([]);
  const [inspirationLoading, setInspirationLoading] = useState(false);
  const [inspirationHasMore, setInspirationHasMore] = useState(false);
  const [inspirationOffset, setInspirationOffset] = useState(0);
  const INSPIRATION_PAGE_SIZE = 20;

  // ── Output state ──────────────────────────────────────────────────────────
  const [outputMode, setOutputMode] = useState<OutputMode>('empty');
  const [ideaCards, setIdeaCards] = useState<IdeaCardData[]>([]);
  const [calendarPosts, setCalendarPosts] = useState<CalendarPostData[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [activeOutputTab, setActiveOutputTab] = useState<'ideas' | 'saved'>('ideas');

  // ── UI state ──────────────────────────────────────────────────────────────
  const [showBrandModal, setShowBrandModal] = useState(false);

  // ── Refs ──────────────────────────────────────────────────────────────────
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatScrollRef = useRef<HTMLDivElement>(null);

  // ── Brand context values ──────────────────────────────────────────────────
  const brandName = brandSettings?.identity?.brandName ?? 'Your brand';
  const contentPillarsRaw = brandSettings?.voice?.contentPillars ?? '';
  const pillarCount = contentPillarsRaw ? contentPillarsRaw.split(',').filter(Boolean).length : 0;
  const brandPillars = contentPillarsRaw
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean);
  // Ideas flow brand-aware chips
  const ideasDirectionChips = getBrandAwareIdeasDirections(brandSettings);
  const ideasAudiencePainChips = getBrandAwareAudiencePain(brandSettings);
  const ideasStyleChips = getBrandAwareIdeaStyles(brandSettings);
  const ideasHookChips = getBrandAwareHookPreferences(brandSettings);

  // Calendar flow brand-aware suggestions
  const suggestedPlatform = getBrandAwarePrimaryPlatform(brandSettings);
  const suggestedGoal = getBrandAwarePrimaryGoal(brandSettings);
  const hookStrategyChips = getBrandAwareHookStrategyOrder(brandSettings);
  const interactionStyleChips = getBrandAwareInteractionStyles(brandSettings);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 50);
  }, []);

  const addMessage = useCallback(
    (msg: Omit<ChatMessageData, 'id'>) => {
      setMessages((prev) => [...prev, { ...msg, id: genId() }]);
      scrollToBottom();
    },
    [scrollToBottom],
  );

  // For conversational steps — shows typing bubble for a brief simulated delay
  const addTypingThenMessage = useCallback(
    async (content: string, element?: MessageElement, delay = 700) => {
      const typingId = genId();
      setMessages((prev) => [...prev, { id: typingId, role: 'ai', content: '', isTyping: true }]);
      scrollToBottom();

      await new Promise((r) => setTimeout(r, delay));

      setMessages((prev) =>
        prev.map((m) => (m.id === typingId ? { ...m, isTyping: false, content, element } : m)),
      );
      scrollToBottom();
    },
    [scrollToBottom],
  );

  // For API-backed responses — shows typing bubble for the duration of the actual call
  const pushTypingBubble = useCallback((): string => {
    const id = genId();
    setMessages((prev) => [...prev, { id, role: 'ai', content: '', isTyping: true }]);
    scrollToBottom();
    return id;
  }, [scrollToBottom]);

  const resolveTypingBubble = useCallback(
    (id: string, content: string, element?: MessageElement) => {
      setMessages((prev) =>
        prev.map((m) => (m.id === id ? { ...m, isTyping: false, content, element } : m)),
      );
      scrollToBottom();
    },
    [scrollToBottom],
  );

  const removeTypingBubble = useCallback((id: string) => {
    setMessages((prev) => prev.filter((m) => m.id !== id));
  }, []);

  // ── Initial greeting ──────────────────────────────────────────────────────
  // Goal picker screen is shown when flowStep === 'start' — no initial message needed.

  // ── New session ───────────────────────────────────────────────────────────
  const handleNewSession = useCallback(() => {
    setMessages([]);
    setFlowStep('start');
    setSelectedGoal(undefined);
    setSelectedPlatforms([]);
    setIdeasFlow({
      direction: '',
      directionOverride: '',
      audiencePain: [],
      audiencePainOverride: '',
      style: [],
      hookPreference: [],
      inspirationMode: 'skip',
      inspirationSeed: '',
      selectedInspirationIds: [],
      constraints: '',
    });
    setCalendarTimeAmount(1);
    setCalendarTimeUnit('weeks');
    setCalendarScheduleType('per-period');
    setCalendarPostsPerPeriod(5);
    setCalendarPostsPerDay(1);
    setCalendarSeedIdeas('');
    setIsGenerating(false);
    setOutputMode('empty');
    setIdeaCards([]);
    setCalendarPosts([]);
    setActiveOutputTab('ideas');
    setCalendarPlan({
      timeframeValue: 1,
      timeframeUnit: 'weeks',
      cadenceType: 'per_week',
      cadenceValue: 5,
      primaryPlatform: '',
      repurpose: false,
      platforms: [],
      goal: '',
      secondaryGoal: '',
      pillarMode: 'default',
      pillarWeights: [],
      pillarIntents: {},
      contentTypeDistribution: { ...DEFAULT_CONTENT_TYPE_DISTRIBUTION },
      hookStrategy: [],
      interactionStyle: [],
      variationLevel: 'medium',
      inspirationMode: 'skip',
      inspirationSeed: '',
      selectedInspirationIds: [],
      constraintsInclude: [],
      constraintsAvoid: [],
      constraints: '',
      contentStyle: [],
      engagementStyle: [],
    });
    setInspirationItems([]);
    setInspirationOffset(0);
    setInspirationHasMore(false);
  }, []);

  // ── Goal selection ────────────────────────────────────────────────────────
  const handleGoalSelect = useCallback(
    async (goal: 'ideas' | 'calendar' | 'draft') => {
      if (flowStep !== 'start') return;
      setSelectedGoal(goal);

      const goalLabels = {
        ideas: 'Generate content ideas',
        calendar: 'Plan a content calendar',
        draft: 'Draft a post from scratch',
      };

      addMessage({ role: 'user', content: goalLabels[goal] });

      if (goal === 'draft') {
        await addTypingThenMessage(
          "Let's open the composer so you can write a post from scratch with AI assistance.",
        );
        navigate('/a/arche_social_media/compose');
        return;
      }

      if (goal === 'ideas') {
        setFlowStep('awaiting-ideas-direction');
        const name = brandSettings?.identity?.brandName;
        await addTypingThenMessage(
          name
            ? `Let's build content ideas for ${name}. What direction should we focus on?`
            : "Let's build some content ideas. What direction should we focus on?",
          'ideas-chips-direction',
        );
      } else {
        // New multi-step calendar flow — Step 1: Timeframe + Cadence
        setFlowStep('awaiting-calendar-timeframe');
        const name = brandSettings?.identity?.brandName;
        await addTypingThenMessage(
          name
            ? `Let's plan ${name}'s content. Set your timeframe and posting cadence below.`
            : "Let's plan your content. Set your timeframe and posting cadence below.",
          'calendar-step-1',
        );
      }
    },
    [flowStep, addMessage, addTypingThenMessage, navigate, brandSettings],
  );

  // ── Ideas flow helpers ────────────────────────────────────────────────────

  const updateIdeasFlow = useCallback((patch: Partial<IdeasFlowState>) => {
    setIdeasFlow((prev) => ({ ...prev, ...patch }));
  }, []);

  // Shared inspiration page fetcher (used by both ideas and calendar flows)
  const fetchInspirationPage = useCallback(
    async (offset: number) => {
      if (inspirationLoading || !selectedHolderId) return;
      setInspirationLoading(true);
      try {
        const result = await socialMediaApi.listInspirationContent(selectedHolderId, {
          sortBy: 'engagement',
          sortDir: 'desc',
          limit: INSPIRATION_PAGE_SIZE,
          offset,
        });
        setInspirationItems((prev) => (offset === 0 ? result.items : [...prev, ...result.items]));
        setInspirationOffset(offset + result.items.length);
        setInspirationHasMore(offset + result.items.length < result.total);
      } catch {
        // silently fail — user can still proceed without items
      } finally {
        setInspirationLoading(false);
      }
    },
    [inspirationLoading, INSPIRATION_PAGE_SIZE, selectedHolderId],
  );

  const handleLoadMoreInspiration = useCallback(() => {
    fetchInspirationPage(inspirationOffset);
  }, [fetchInspirationPage, inspirationOffset]);

  // ── Generate ideas ────────────────────────────────────────────────────────
  const generateIdeas = useCallback(
    async (flow: IdeasFlowState, feedback?: string) => {
      setIsGenerating(true);
      setFlowStep('generating');

      const typingId = pushTypingBubble();

      const selectedInspirationContent =
        flow.inspirationMode === 'use' && flow.selectedInspirationIds.length > 0
          ? flow.selectedInspirationIds
              .map((id) => inspirationItems.find((item) => item.contentId === id))
              .filter((item): item is NonNullable<typeof item> => item != null)
              .map((item) => ({
                author: item.author,
                content: item.contentPreview,
                platform: item.platform,
              }))
          : undefined;

      const ideasContext = {
        direction: flow.direction || flow.directionOverride || 'Mixed',
        directionOverride: flow.directionOverride || undefined,
        audiencePain: flow.audiencePain.length > 0 ? flow.audiencePain : undefined,
        audiencePainOverride: flow.audiencePainOverride || undefined,
        style: flow.style.length > 0 ? flow.style : undefined,
        hookPreference: flow.hookPreference.length > 0 ? flow.hookPreference : undefined,
        inspirationMode: flow.inspirationMode,
        inspirationSeed:
          flow.inspirationMode === 'use' && flow.inspirationSeed.trim()
            ? flow.inspirationSeed.trim()
            : undefined,
        selectedInspirationIds:
          flow.inspirationMode === 'use' && flow.selectedInspirationIds.length > 0
            ? flow.selectedInspirationIds
            : undefined,
        selectedInspirationContent,
        constraints: flow.constraints.trim() || undefined,
        feedback,
      };

      console.log('[WriterTable] Ideas generation context:', ideasContext);

      if (!selectedHolderId) {
        toast({
          title: 'Pick a workspace',
          description: 'Select a workspace from the switcher to generate ideas.',
          variant: 'destructive',
        });
        setIsGenerating(false);
        return;
      }
      try {
        const result = await socialMediaApi.generateWriterIdeas(selectedHolderId, {
          direction: ideasContext.direction,
          directionOverride: ideasContext.directionOverride,
          audiencePain: ideasContext.audiencePain,
          audiencePainOverride: ideasContext.audiencePainOverride,
          style: ideasContext.style,
          hookPreference: ideasContext.hookPreference,
          inspirationSeed: ideasContext.inspirationSeed,
          selectedInspirationIds: ideasContext.selectedInspirationIds,
          selectedInspirationContent: ideasContext.selectedInspirationContent,
          constraints: ideasContext.constraints,
          feedback: ideasContext.feedback,
        });

        const cards: IdeaCardData[] = result.ideas.map((idea) => ({
          id: genId(),
          idea,
          saved: false,
          isRegenerating: false,
        }));

        setIdeaCards(cards);
        setOutputMode('ideas');
        setActiveOutputTab('ideas');
        setFlowStep('done');

        resolveTypingBubble(
          typingId,
          `Generated ${cards.length} content ideas — check them out in the panel →`,
        );
      } catch {
        removeTypingBubble(typingId);
        toast({
          title: 'Generation failed',
          description: 'Could not generate ideas. Please try again.',
          variant: 'destructive',
        });
        setFlowStep('awaiting-ideas-confirm');
      } finally {
        setIsGenerating(false);
      }
    },
    [pushTypingBubble, resolveTypingBubble, removeTypingBubble, inspirationItems],
  );

  // ── Ideas flow step handlers ──────────────────────────────────────────────

  const handleIdeasDirectionSelect = useCallback(
    async (direction: string) => {
      updateIdeasFlow({ direction });
      addMessage({ role: 'user', content: direction });
      setFlowStep('awaiting-ideas-audience');
      await addTypingThenMessage(
        'Who are you writing for? What should these ideas resonate with?',
        'ideas-chips-audience',
      );
    },
    [updateIdeasFlow, addMessage, addTypingThenMessage],
  );

  const handleIdeasAudienceNext = useCallback(async () => {
    const label =
      ideasFlow.audiencePain.length > 0
        ? ideasFlow.audiencePain.join(', ')
        : ideasFlow.audiencePainOverride.trim() || 'General audience';
    addMessage({ role: 'user', content: label });
    setFlowStep('awaiting-ideas-style');
    await addTypingThenMessage('What content style should we lean into?', 'ideas-chips-style');
  }, [ideasFlow, addMessage, addTypingThenMessage]);

  const handleIdeasStyleNext = useCallback(async () => {
    const label = ideasFlow.style.length > 0 ? ideasFlow.style.join(', ') : 'No style preference';
    addMessage({ role: 'user', content: label });
    setFlowStep('awaiting-ideas-hook');
    await addTypingThenMessage('What kind of hooks should we open with?', 'ideas-chips-hook');
  }, [ideasFlow, addMessage, addTypingThenMessage]);

  const handleIdeasHookNext = useCallback(async () => {
    const label =
      ideasFlow.hookPreference.length > 0 ? ideasFlow.hookPreference.join(', ') : 'No preference';
    addMessage({ role: 'user', content: label });
    setFlowStep('awaiting-ideas-inspiration');
    await addTypingThenMessage(
      'Want to pull in inspiration from your feed, or start fresh?',
      'ideas-chips-inspiration',
    );
  }, [ideasFlow, addMessage, addTypingThenMessage]);

  const handleIdeasInspirationModeChange = useCallback(
    (mode: 'use' | 'skip') => {
      updateIdeasFlow({ inspirationMode: mode });
      if (mode === 'use' && inspirationItems.length === 0) {
        fetchInspirationPage(0);
      }
    },
    [updateIdeasFlow, inspirationItems.length, fetchInspirationPage],
  );

  const handleIdeasInspirationItemToggle = useCallback(
    (id: string) => {
      updateIdeasFlow({
        selectedInspirationIds: ideasFlow.selectedInspirationIds.includes(id)
          ? ideasFlow.selectedInspirationIds.filter((x) => x !== id)
          : [...ideasFlow.selectedInspirationIds, id],
      });
    },
    [ideasFlow.selectedInspirationIds, updateIdeasFlow],
  );

  const handleIdeasInspirationNext = useCallback(async () => {
    const label =
      ideasFlow.inspirationMode === 'skip'
        ? 'Starting from scratch'
        : ideasFlow.selectedInspirationIds.length > 0
          ? `${ideasFlow.selectedInspirationIds.length} item${ideasFlow.selectedInspirationIds.length !== 1 ? 's' : ''} from feed`
          : ideasFlow.inspirationSeed.trim()
            ? 'Seed text provided'
            : 'Starting from scratch';
    addMessage({ role: 'user', content: label });
    setFlowStep('awaiting-ideas-constraints');
    await addTypingThenMessage('Anything specific to include or avoid?', 'ideas-constraints-input');
  }, [ideasFlow, addMessage, addTypingThenMessage]);

  const handleIdeasConstraintsNext = useCallback(async () => {
    const label = ideasFlow.constraints.trim() || 'No constraints';
    addMessage({ role: 'user', content: label });
    setFlowStep('awaiting-ideas-confirm');
    await addTypingThenMessage(
      "Here's your idea brief — ready to generate when you are:",
      'ideas-confirm',
    );
  }, [ideasFlow, addMessage, addTypingThenMessage]);

  // ── Platform picker confirm (legacy — not used in new flow) ───────────────
  const handlePlatformConfirm = useCallback(() => {
    const platformText =
      selectedPlatforms.length > 0 ? selectedPlatforms.join(', ') : 'all platforms';
    addMessage({ role: 'user', content: `Platforms: ${platformText}` });
  }, [selectedPlatforms, addMessage]);

  // ── Calendar planning step helpers ────────────────────────────────────────

  const updateCalendarPlan = useCallback((patch: Partial<CalendarPlanState>) => {
    setCalendarPlan((prev) => ({ ...prev, ...patch }));
  }, []);

  // Step 1: Timeframe + Cadence → Step 2: Platform
  const handleCalendarStep1Next = useCallback(async () => {
    const { timeframeValue, timeframeUnit, cadenceType, cadenceValue } = calendarPlan;
    const cadenceLabel =
      cadenceType === 'per_week' ? `${cadenceValue} posts/week` : `${cadenceValue} posts/day`;
    addMessage({ role: 'user', content: `${timeframeValue} ${timeframeUnit} · ${cadenceLabel}` });
    setFlowStep('awaiting-calendar-platforms');
    await addTypingThenMessage(
      `Which platform is your **primary focus** for this calendar?\n\nWe'll optimize hooks, captions, and format specifically for it. If you also want to adapt content to other platforms, enable repurposing below — posts will allow slight format variations.\n\n_e.g. Long-form takes on LinkedIn, punchy one-liners on X — same idea, different packaging._`,
      'calendar-platform-primary',
    );
  }, [calendarPlan, addMessage, addTypingThenMessage]);

  // Step 2: Platform → Step 3: Goal
  const handleCalendarPlatformPrimarySelect = useCallback(
    (platform: string) => {
      updateCalendarPlan({ primaryPlatform: platform, platforms: [platform] });
    },
    [updateCalendarPlan],
  );

  const handleCalendarRepurposeToggle = useCallback(() => {
    updateCalendarPlan({ repurpose: !calendarPlan.repurpose });
  }, [calendarPlan.repurpose, updateCalendarPlan]);

  const handleCalendarPlatformPrimaryNext = useCallback(async () => {
    const label = calendarPlan.repurpose
      ? `${calendarPlan.primaryPlatform} (+ repurpose)`
      : calendarPlan.primaryPlatform;
    addMessage({ role: 'user', content: label });
    setFlowStep('awaiting-calendar-goal');
    await addTypingThenMessage(
      `What's the **main goal** for this content cycle?\n\nThis shapes how aggressive hooks get, what CTA types we use, and how we weight content types.\n\n• **Engagement** → questions, debate hooks, share prompts\n• **Growth** → curiosity gaps, viral formats, relatable takes\n• **Authority** → strong statements, data-backed posts, minimal CTAs\n• **Conversion** → direct asks, product-led content, social proof\n\nYou can also add a secondary goal or specific campaign focus below.`,
      'calendar-goal-secondary',
    );
  }, [calendarPlan, addMessage, addTypingThenMessage]);

  // Step 3: Goal → Step 4: Pillars
  const handleCalendarPrimaryGoalSelect = useCallback(
    async (goal: string) => {
      // Initialize pillar weights from brand settings when goal is selected
      const pillarsRaw = brandSettings?.voice?.contentPillars ?? '';
      const pillars = pillarsRaw
        .split(',')
        .map((p) => p.trim())
        .filter(Boolean);
      const patch: Partial<CalendarPlanState> = { goal };
      if (pillars.length > 0 && calendarPlan.pillarWeights.length === 0) {
        const equalWeight = Math.round(100 / pillars.length);
        patch.pillarWeights = pillars.map((name) => ({ name, weight: equalWeight }));
      }
      if (Object.keys(calendarPlan.pillarIntents).length === 0 && pillars.length > 0) {
        patch.pillarIntents = getBrandAwarePillarIntents(brandSettings);
      }
      if (Object.values(calendarPlan.contentTypeDistribution).every((v) => v === 20)) {
        patch.contentTypeDistribution = getBrandAwareContentTypeDistribution(brandSettings);
      }
      if (calendarPlan.hookStrategy.length === 0) {
        patch.hookStrategy = getBrandAwareDefaultHookStrategy(brandSettings);
      }
      if (calendarPlan.interactionStyle.length === 0) {
        patch.interactionStyle = getBrandAwareDefaultInteractionStyles(brandSettings);
      }
      patch.variationLevel = getBrandAwareVariationLevel(brandSettings);
      updateCalendarPlan(patch);
    },
    [updateCalendarPlan, brandSettings, calendarPlan],
  );

  const handleCalendarGoalNext = useCallback(async () => {
    const label = calendarPlan.secondaryGoal.trim()
      ? `${calendarPlan.goal} · ${calendarPlan.secondaryGoal.trim()}`
      : calendarPlan.goal;
    addMessage({ role: 'user', content: label });
    setFlowStep('awaiting-calendar-pillars');
    await addTypingThenMessage(
      `Here are your content pillars. Set how much weight each one gets in this calendar, and assign an **intent** so the AI knows what you're trying to do with each one.\n\n_e.g. "BTS" → Document · "Tips" → Teach · "Hot takes" → Provoke_\n\nSkipping intent is fine — the AI will infer it from your brand voice.`,
      'calendar-chips-pillars',
    );
  }, [calendarPlan, addMessage, addTypingThenMessage]);

  // Step 4: Pillars → Step 5: Content Type Distribution
  const handleCalendarPillarModeChange = useCallback(
    (mode: 'default' | 'adjust') => {
      updateCalendarPlan({ pillarMode: mode });
    },
    [updateCalendarPlan],
  );

  const handleCalendarPillarWeightChange = useCallback(
    (name: string, weight: number) => {
      updateCalendarPlan({
        pillarWeights: calendarPlan.pillarWeights.map((pw) =>
          pw.name === name ? { ...pw, weight } : pw,
        ),
      });
    },
    [calendarPlan.pillarWeights, updateCalendarPlan],
  );

  const handleCalendarPillarIntentChange = useCallback(
    (pillar: string, intent: string) => {
      updateCalendarPlan({
        pillarIntents: { ...calendarPlan.pillarIntents, [pillar]: intent },
      });
    },
    [calendarPlan.pillarIntents, updateCalendarPlan],
  );

  const handleCalendarPillarsNext = useCallback(async () => {
    const label =
      calendarPlan.pillarMode === 'default'
        ? 'Using default pillar mix'
        : `Custom: ${calendarPlan.pillarWeights
            .filter((p) => p.weight > 0)
            .map((p) => `${p.name} ${p.weight}%`)
            .join(', ')}`;
    addMessage({ role: 'user', content: label });
    setFlowStep('awaiting-calendar-content-type');
    await addTypingThenMessage(
      `How should we distribute your **content types**? These control how posts are framed — not just what they're about.\n\n• **Story / Narrative** → wrap insight in a personal arc\n• **Insight / Lessons** → share what you've learned\n• **Opinion / Hot takes** → stake a position, invite reaction\n• **Tactical / How-to** → step-by-step value delivery\n• **Relatable / Observational** → "you know that feeling when…"\n\nAdjust sliders to define your mix. Total should equal 100%.`,
      'calendar-content-type',
    );
  }, [calendarPlan, addMessage, addTypingThenMessage]);

  // Step 5: Content Type → Step 6: Hook Strategy
  const handleCalendarContentTypeChange = useCallback(
    (type: string, value: number) => {
      updateCalendarPlan({
        contentTypeDistribution: { ...calendarPlan.contentTypeDistribution, [type]: value },
      });
    },
    [calendarPlan.contentTypeDistribution, updateCalendarPlan],
  );

  const handleCalendarContentTypeNext = useCallback(async () => {
    const top = CONTENT_TYPE_LABELS.filter(
      (t) => (calendarPlan.contentTypeDistribution[t] ?? 0) > 0,
    )
      .sort(
        (a, b) =>
          (calendarPlan.contentTypeDistribution[b] ?? 0) -
          (calendarPlan.contentTypeDistribution[a] ?? 0),
      )
      .slice(0, 2)
      .map((t) => `${t} (${calendarPlan.contentTypeDistribution[t]}%)`)
      .join(', ');
    addMessage({ role: 'user', content: top || 'Balanced mix' });
    setFlowStep('awaiting-calendar-hook-strategy');
    await addTypingThenMessage(
      `Which **hook styles** should this calendar lean into most? Select them in priority order — we'll use your top picks more often.\n\n• **Contrarian** → spike reach and debate\n• **Curiosity gap** → boost saves and clicks\n• **Problem-first** → high comment engagement\n• **Story-led** → strongest emotional connection\n• **Authority / Results** → builds credibility\n\n_Tip: Your top 3 dominate. A mix of one emotion-based and one value-based hook usually performs best._`,
      'calendar-hook-strategy',
    );
  }, [calendarPlan, addMessage, addTypingThenMessage]);

  // Step 6: Hook Strategy → Step 7: Interaction Style
  const handleCalendarHookStrategyToggle = useCallback(
    (hook: string) => {
      const current = calendarPlan.hookStrategy;
      updateCalendarPlan({
        hookStrategy: current.includes(hook)
          ? current.filter((h) => h !== hook)
          : [...current, hook],
      });
    },
    [calendarPlan.hookStrategy, updateCalendarPlan],
  );

  const handleCalendarHookStrategyNext = useCallback(async () => {
    const label =
      calendarPlan.hookStrategy.length > 0
        ? calendarPlan.hookStrategy.slice(0, 3).join(', ')
        : 'No preference';
    addMessage({ role: 'user', content: label });
    setFlowStep('awaiting-calendar-interaction');
    await addTypingThenMessage(
      `How should posts drive interaction? Not every post should feel the same — we'll rotate these across the calendar.\n\n• **Passive** → strong statement, no ask — lets the content do the work\n• **Light** → "save this / follow for more" — low friction\n• **Conversational** → "what's your experience with X?" — drives comments\n• **Debate-driven** → "controversial take: [opinion]" — high engagement\n• **Aggressive** → direct DM or link CTA — conversion-focused\n• **Mix** → let the AI blend all styles intelligently\n\nSelect the styles you want rotated across the calendar.`,
      'calendar-interaction-style',
    );
  }, [calendarPlan.hookStrategy, addMessage, addTypingThenMessage]);

  // Step 7: Interaction Style → Step 8: Variation
  const handleCalendarInteractionStyleToggle = useCallback(
    (style: string) => {
      updateCalendarPlan({
        interactionStyle: calendarPlan.interactionStyle.includes(style)
          ? calendarPlan.interactionStyle.filter((s) => s !== style)
          : [...calendarPlan.interactionStyle, style],
      });
    },
    [calendarPlan.interactionStyle, updateCalendarPlan],
  );

  const handleCalendarInteractionStyleNext = useCallback(async () => {
    const label =
      calendarPlan.interactionStyle.length > 0
        ? calendarPlan.interactionStyle.join(', ')
        : 'No preference';
    addMessage({ role: 'user', content: label });
    setFlowStep('awaiting-calendar-variation');
    await addTypingThenMessage(
      `How **experimental** should the calendar feel?\n\n• **Low** → consistent tone, predictable structure — great for trust-building\n• **Medium** → balanced: some consistency, some planned spikes — works for most accounts\n• **High** → aggressive hook diversity, emotional contrast, format experiments — viral-first\n\n_Most growing accounts benefit from Medium. Use High if you're in a growth sprint or testing new content territory._`,
      'calendar-variation',
    );
  }, [calendarPlan.interactionStyle, addMessage, addTypingThenMessage]);

  // Step 8: Variation → Step 9: Source Input
  const handleCalendarVariationSelect = useCallback(
    (level: 'low' | 'medium' | 'high') => {
      updateCalendarPlan({ variationLevel: level });
    },
    [updateCalendarPlan],
  );

  const handleCalendarVariationNext = useCallback(async () => {
    addMessage({ role: 'user', content: `Variation: ${calendarPlan.variationLevel}` });
    setFlowStep('awaiting-calendar-inspiration');
    await addTypingThenMessage(
      `Do you want to seed the calendar with specific ideas, pull from your **inspiration feed**, or generate everything from scratch?\n\n• **Seed ideas** → add topics or concepts you've been sitting on\n• **Inspiration feed** → remix from competitor or creator content you've saved\n• **From scratch** → fully generated from your pillars, goal, and settings`,
      'calendar-chips-inspiration',
    );
  }, [calendarPlan.variationLevel, addMessage, addTypingThenMessage]);

  // Step 9: Source Input → Step 10: Constraints
  const handleCalendarInspirationModeChange = useCallback(
    (mode: 'use' | 'skip') => {
      updateCalendarPlan({ inspirationMode: mode });
      if (mode === 'use' && inspirationItems.length === 0) {
        fetchInspirationPage(0);
      }
    },
    [updateCalendarPlan, inspirationItems.length, fetchInspirationPage],
  );

  const handleInspirationItemToggle = useCallback(
    (id: string) => {
      updateCalendarPlan({
        selectedInspirationIds: calendarPlan.selectedInspirationIds.includes(id)
          ? calendarPlan.selectedInspirationIds.filter((x) => x !== id)
          : [...calendarPlan.selectedInspirationIds, id],
      });
    },
    [calendarPlan.selectedInspirationIds, updateCalendarPlan],
  );

  const handleCalendarInspirationNext = useCallback(async () => {
    const label =
      calendarPlan.inspirationMode === 'use' && calendarPlan.selectedInspirationIds.length > 0
        ? `${calendarPlan.selectedInspirationIds.length} items from feed`
        : calendarPlan.inspirationMode === 'use' && calendarPlan.inspirationSeed.trim()
          ? `Seed: "${calendarPlan.inspirationSeed.slice(0, 60)}…"`
          : 'From scratch';
    addMessage({ role: 'user', content: label });
    setFlowStep('awaiting-calendar-constraints');
    await addTypingThenMessage(
      `Anything to specifically **include or avoid** across the calendar? These apply globally to all posts.\n\n_e.g. Include "personal stories" if authenticity is a priority · Avoid "clickbait" if you have a professional audience_\n\nYou can also add a custom note below.`,
      'calendar-constraints-ia',
    );
  }, [calendarPlan, addMessage, addTypingThenMessage]);

  // Step 10: Constraints → Confirm
  const handleCalendarIncludeToggle = useCallback(
    (item: string) => {
      updateCalendarPlan({
        constraintsInclude: calendarPlan.constraintsInclude.includes(item)
          ? calendarPlan.constraintsInclude.filter((x) => x !== item)
          : [...calendarPlan.constraintsInclude, item],
      });
    },
    [calendarPlan.constraintsInclude, updateCalendarPlan],
  );

  const handleCalendarAvoidToggle = useCallback(
    (item: string) => {
      updateCalendarPlan({
        constraintsAvoid: calendarPlan.constraintsAvoid.includes(item)
          ? calendarPlan.constraintsAvoid.filter((x) => x !== item)
          : [...calendarPlan.constraintsAvoid, item],
      });
    },
    [calendarPlan.constraintsAvoid, updateCalendarPlan],
  );

  const handleCalendarConstraintsNext = useCallback(async () => {
    const parts: string[] = [];
    if (calendarPlan.constraintsInclude.length > 0)
      parts.push(`Include: ${calendarPlan.constraintsInclude.join(', ')}`);
    if (calendarPlan.constraintsAvoid.length > 0)
      parts.push(`Avoid: ${calendarPlan.constraintsAvoid.join(', ')}`);
    if (calendarPlan.constraints.trim()) parts.push(calendarPlan.constraints.trim());
    addMessage({ role: 'user', content: parts.join(' · ') || 'No constraints' });
    setFlowStep('awaiting-calendar-confirm');
    await addTypingThenMessage(
      `Here's your full content calendar plan — ready to generate when you are:`,
      'calendar-confirm',
    );
  }, [calendarPlan, addMessage, addTypingThenMessage]);

  // ── Calendar generate (new flow) ──────────────────────────────────────────
  const handleCalendarGenerate = useCallback(async () => {
    setIsGenerating(true);
    setFlowStep('generating');

    const typingId = pushTypingBubble();

    const scheduleType: 'per-period' | 'daily' =
      calendarPlan.cadenceType === 'per_week' ? 'per-period' : 'daily';
    const timeUnit: 'weeks' | 'months' =
      calendarPlan.timeframeUnit === 'months' ? 'months' : 'weeks';
    const timeAmount =
      calendarPlan.timeframeUnit === 'days'
        ? Math.max(1, Math.round(calendarPlan.timeframeValue / 7))
        : calendarPlan.timeframeValue;

    const selectedInspirationContent =
      calendarPlan.inspirationMode === 'use' && calendarPlan.selectedInspirationIds.length > 0
        ? calendarPlan.selectedInspirationIds
            .map((id) => inspirationItems.find((item) => item.contentId === id))
            .filter((item): item is NonNullable<typeof item> => item != null)
            .map((item) => ({
              author: item.author,
              content: item.contentPreview,
              platform: item.platform,
            }))
        : undefined;

    // Build enriched constraints string from new include/avoid + free text
    const constraintsString =
      [
        calendarPlan.constraintsInclude.length > 0
          ? `Include: ${calendarPlan.constraintsInclude.join(', ')}`
          : null,
        calendarPlan.constraintsAvoid.length > 0
          ? `Avoid: ${calendarPlan.constraintsAvoid.join(', ')}`
          : null,
        calendarPlan.constraints.trim() || null,
      ]
        .filter(Boolean)
        .join('. ') || undefined;

    // Build enriched engagement/content style from new fields
    const engagementStyleContext =
      calendarPlan.interactionStyle.length > 0
        ? calendarPlan.interactionStyle
        : calendarPlan.engagementStyle.length > 0
          ? calendarPlan.engagementStyle
          : undefined;

    const contentStyleContext = CONTENT_TYPE_LABELS.filter(
      (t) => (calendarPlan.contentTypeDistribution[t] ?? 0) > 0,
    ).map((t) => `${t} ${calendarPlan.contentTypeDistribution[t]}%`);

    const calendarContext = {
      timeAmount,
      timeUnit,
      scheduleType,
      postsPerPeriod: scheduleType === 'per-period' ? calendarPlan.cadenceValue : 5,
      postsPerDay: scheduleType === 'daily' ? calendarPlan.cadenceValue : 1,
      platforms: calendarPlan.primaryPlatform ? [calendarPlan.primaryPlatform] : undefined,
      inspirationMode: calendarPlan.inspirationMode,
      seedIdeas:
        calendarPlan.inspirationMode === 'use' && calendarPlan.inspirationSeed.trim()
          ? calendarPlan.inspirationSeed.trim()
          : undefined,
      selectedInspirationContent,
      goal: calendarPlan.secondaryGoal.trim()
        ? `${calendarPlan.goal} — ${calendarPlan.secondaryGoal.trim()}`
        : calendarPlan.goal || undefined,
      contentStyle: contentStyleContext.length > 0 ? contentStyleContext : undefined,
      engagementStyle: engagementStyleContext,
      constraints: constraintsString,
      pillars:
        calendarPlan.pillarMode === 'adjust' && calendarPlan.pillarWeights.length > 0
          ? calendarPlan.pillarWeights
          : undefined,
    };

    console.log('[WriterTable] Calendar generation context:', {
      ...calendarContext,
      hookStrategy: calendarPlan.hookStrategy,
      variationLevel: calendarPlan.variationLevel,
      pillarIntents: calendarPlan.pillarIntents,
      repurpose: calendarPlan.repurpose,
    });

    if (!selectedHolderId) {
      toast({
        title: 'Pick a workspace',
        description: 'Select a workspace from the switcher to generate a calendar.',
        variant: 'destructive',
      });
      return;
    }
    try {
      const result = await socialMediaApi.generateWriterCalendar(selectedHolderId, {
        timeAmount: calendarContext.timeAmount,
        timeUnit: calendarContext.timeUnit,
        scheduleType: calendarContext.scheduleType,
        postsPerPeriod: calendarContext.postsPerPeriod,
        postsPerDay: calendarContext.postsPerDay,
        platforms: calendarContext.platforms,
        seedIdeas: calendarContext.seedIdeas,
        selectedInspirationContent: calendarContext.selectedInspirationContent,
        goal: calendarContext.goal,
        contentStyle: calendarContext.contentStyle,
        engagementStyle: calendarContext.engagementStyle,
        constraints: calendarContext.constraints,
        pillars: calendarContext.pillars,
      });

      const posts: CalendarPostData[] = result.posts.map((post) => ({
        ...post,
        id: genId(),
        saved: false,
      }));

      setCalendarPosts(posts);
      setOutputMode('calendar');
      setActiveOutputTab('ideas');
      setFlowStep('done');

      resolveTypingBubble(
        typingId,
        `Your content calendar is ready — ${posts.length} posts planned. Check it out in the panel →`,
        'follow-up-chips-calendar',
      );
    } catch {
      removeTypingBubble(typingId);
      toast({
        title: 'Generation failed',
        description: 'Could not generate the calendar. Please try again.',
        variant: 'destructive',
      });
      setFlowStep('awaiting-calendar-confirm');
    } finally {
      setIsGenerating(false);
    }
  }, [calendarPlan, pushTypingBubble, resolveTypingBubble, removeTypingBubble, inspirationItems]);

  // ── Topic submit (legacy — no longer used in new 7-step ideas flow) ─────────
  const handleTopicSubmit = useCallback((_text: string) => {
    // no-op: topic-input element is not emitted in the new ideas flow
  }, []);

  // ── Chip click (follow-up) ────────────────────────────────────────────────
  const handleChipClick = useCallback(
    (chip: string) => {
      addMessage({ role: 'user', content: chip });
      addTypingThenMessage(
        'Use the Regenerate bar in the panel to refine your ideas, or start a new session.',
      );
    },
    [addMessage, addTypingThenMessage],
  );

  // ── Platform toggle ───────────────────────────────────────────────────────
  const togglePlatform = (p: string) => {
    setSelectedPlatforms((prev) => (prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]));
  };

  // ── Idea card actions ─────────────────────────────────────────────────────
  const handleRegenerateCard = useCallback(
    async (id: string) => {
      const card = ideaCards.find((c) => c.id === id);
      if (!card || !selectedHolderId) return;

      setIdeaCards((prev) => prev.map((c) => (c.id === id ? { ...c, isRegenerating: true } : c)));

      try {
        const result = await socialMediaApi.regenerateWriterIdea(selectedHolderId, {
          direction: ideasFlow.direction || ideasFlow.directionOverride || 'Mixed',
          pillar: card.idea.pillar,
          audiencePain: ideasFlow.audiencePain.length > 0 ? ideasFlow.audiencePain : undefined,
          style: ideasFlow.style.length > 0 ? ideasFlow.style : undefined,
          hookPreference:
            ideasFlow.hookPreference.length > 0 ? ideasFlow.hookPreference : undefined,
          constraints: ideasFlow.constraints.trim() || undefined,
        });

        setIdeaCards((prev) =>
          prev.map((c) => (c.id === id ? { ...c, idea: result.idea, isRegenerating: false } : c)),
        );
      } catch {
        toast({ title: 'Regeneration failed', variant: 'destructive' });
        setIdeaCards((prev) =>
          prev.map((c) => (c.id === id ? { ...c, isRegenerating: false } : c)),
        );
      }
    },
    [ideaCards, ideasFlow, selectedHolderId],
  );

  const handleDeleteCard = (id: string) => {
    setIdeaCards((prev) => prev.filter((c) => c.id !== id));
  };

  const handleSaveDraft = useCallback(
    async (id: string) => {
      const card = ideaCards.find((c) => c.id === id);
      if (!card || card.saved || !selectedHolderId) return;

      try {
        const content = `${card.idea.hook}\n\n${card.idea.concept}\n\n${card.idea.angle}`.trim();
        await socialMediaApi.createSocialMediaPost(selectedHolderId, { originalContent: content });
        setIdeaCards((prev) => prev.map((c) => (c.id === id ? { ...c, saved: true } : c)));
      } catch {
        toast({ title: 'Failed to save draft', variant: 'destructive' });
      }
    },
    [ideaCards, selectedHolderId],
  );

  const handleOpenComposer = useCallback(
    async (id: string) => {
      const card = ideaCards.find((c) => c.id === id);
      if (!card || !selectedHolderId) return;

      try {
        const content = `${card.idea.hook}\n\n${card.idea.concept}\n\n${card.idea.angle}`.trim();
        const post = await socialMediaApi.createSocialMediaPost(selectedHolderId, {
          originalContent: content,
        });
        navigate(`/a/arche_social_media/compose/${post.postId}`);
      } catch {
        toast({ title: 'Failed to open composer', variant: 'destructive' });
      }
    },
    [ideaCards, navigate, selectedHolderId],
  );

  // ── Calendar post actions ─────────────────────────────────────────────────
  const handleSaveCalendarDraft = useCallback(
    async (id: string) => {
      const post = calendarPosts.find((p) => p.id === id);
      if (!post || post.saved || !selectedHolderId) return;

      try {
        await socialMediaApi.createSocialMediaPost(selectedHolderId, {
          originalContent: `${post.hook}\n\n${post.copy}`,
        });
        setCalendarPosts((prev) => prev.map((p) => (p.id === id ? { ...p, saved: true } : p)));
      } catch {
        toast({ title: 'Failed to save draft', variant: 'destructive' });
      }
    },
    [calendarPosts, selectedHolderId],
  );

  const handleOpenCalendarComposer = useCallback(
    async (id: string) => {
      const post = calendarPosts.find((p) => p.id === id);
      if (!post || !selectedHolderId) return;

      try {
        const created = await socialMediaApi.createSocialMediaPost(selectedHolderId, {
          originalContent: `${post.hook}\n\n${post.copy}`,
        });
        navigate(`/a/arche_social_media/compose/${created.postId}`);
      } catch {
        toast({ title: 'Failed to open composer', variant: 'destructive' });
      }
    },
    [calendarPosts, navigate, selectedHolderId],
  );

  const handleRegenerateCalendarPost = (_id: string) => {
    toast({ title: 'Use "New session" in the chat to rebuild the calendar plan.' });
  };

  // ── Save all drafts ───────────────────────────────────────────────────────
  const handleSaveAllDrafts = useCallback(async () => {
    if (outputMode === 'ideas') {
      const unsaved = ideaCards.filter((c) => !c.saved);
      for (const card of unsaved) {
        await handleSaveDraft(card.id);
      }
    } else if (outputMode === 'calendar') {
      const unsaved = calendarPosts.filter((p) => !p.saved);
      for (const post of unsaved) {
        await handleSaveCalendarDraft(post.id);
      }
    }
  }, [outputMode, ideaCards, calendarPosts, handleSaveDraft, handleSaveCalendarDraft]);

  // ── Derived counts ────────────────────────────────────────────────────────
  const totalGenerated = outputMode === 'ideas' ? ideaCards.length : calendarPosts.length;
  const totalSaved =
    outputMode === 'ideas'
      ? ideaCards.filter((c) => c.saved).length
      : calendarPosts.filter((p) => p.saved).length;

  // ── Output header text ────────────────────────────────────────────────────
  const outputTitle =
    outputMode === 'empty'
      ? 'Waiting for your prompt…'
      : outputMode === 'ideas'
        ? `${ideaCards.length} content idea${ideaCards.length !== 1 ? 's' : ''}`
        : `${calendarPosts.length}-post content plan`;

  const outputSubtitle =
    outputMode === 'empty'
      ? 'Generated content will appear here'
      : outputMode === 'ideas'
        ? `5 ideas · ${brandName} brand voice · ${ideasFlow.direction || 'Mixed direction'}`
        : `${calendarPosts.length} posts · Pillar-balanced `;

  // ── Group calendar posts by day ───────────────────────────────────────────
  const calendarByDay = calendarPosts.reduce<Record<string, CalendarPostData[]>>((acc, post) => {
    if (!acc[post.day]) acc[post.day] = [];
    acc[post.day].push(post);
    return acc;
  }, {});

  const calendarDays = Object.keys(calendarByDay);

  return (
    <>
      {/* Keyframes injected via a style tag */}
      <style>{`
        @keyframes writerFadeUp {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {showBrandModal && (
        <BrandVoiceModal brandSettings={brandSettings} onClose={() => setShowBrandModal(false)} />
      )}

      {/* ── Goal Picker (start state) ─────────────────────────────────────── */}
      {flowStep === 'start' && (
        <div className="relative w-full h-full overflow-hidden">
          {/* Animated Background Layer */}
          <div className="absolute inset-0 animated-bg">
            <div className="blob blob-1" />
            <div className="blob blob-2" />
            <div className="blob blob-3" />
          </div>

          {/* Glass Foreground */}
          <div className="relative z-10 flex flex-col h-full items-center justify-center">
            <div className="glass-container w-full h-full flex flex-col items-center justify-center">
              <div className="mb-10 text-center flex flex-col gap-2 lg:px-0 px-8">
                <p className="text-4xl font-semibold text-primary font-satoshi">
                  What would you like to create?
                </p>
                <p className="text-[18px] text-primary/40 mt-1.5 font-satoshi">
                  Choose a starting point
                </p>
              </div>

              <div
                className="flex lg:flex-row flex-col lg:px-0 px-7 gap-4 w-full"
                style={{ maxWidth: 880 }}
              >
                {GOALS.map((goal) => (
                  <button
                    key={goal.id}
                    type="button"
                    onClick={() => handleGoalSelect(goal.id)}
                    className="flex-1 flex flex-col lg:items-center items-start lg:gap-3 gap-1 rounded-[14px] border border-white/20 bg-white/10 backdrop-blur-md lg:p-7 p-3 lg:text-center text-left cursor-pointer transition-all duration-300 shadow-lg hover:border-white/40 hover:-translate-y-1 hover:bg-white/20"
                  >
                    <span
                      className="w-14 h-14 rounded-2xl flex items-center justify-center"
                      style={{ background: goal.iconBg }}
                    >
                      {goal.icon}
                    </span>

                    <span className="block text-[14px] font-semibold text-primary">
                      {goal.label}
                    </span>

                    <span className="block text-[12px] text-primary/70 leading-relaxed mt-0.5">
                      {goal.description}
                    </span>
                  </button>
                ))}
              </div>

              {brandSettings && (
                <div className="mt-8">
                  <BrandContextPill
                    brandName={brandName}
                    pillarCount={pillarCount}
                    onClick={() => setShowBrandModal(true)}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Active: single-column chat + results ─────────────────────────── */}
      {flowStep !== 'start' && (
        <div className="flex flex-col h-full overflow-hidden bg-social-bg">
          {/* Header */}
          <div
            className="flex-shrink-0 border-b border-border"
            style={{ padding: '12px 20px', background: 'var(--card)' }}
          >
            <div
              className="flex items-center justify-between"
              style={{ maxWidth: 760, margin: '0 auto' }}
            >
              {brandSettings ? (
                <BrandContextPill
                  brandName={brandName}
                  pillarCount={pillarCount}
                  onClick={() => setShowBrandModal(true)}
                />
              ) : (
                <div />
              )}
              <button
                type="button"
                onClick={handleNewSession}
                title="New session"
                className="rounded-md flex items-center gap-1 text-muted-foreground hover:bg-muted transition-colors font-mono text-[10px] px-1.5 py-1"
              >
                <RotateCcw size={13} />
                Restart
              </button>
            </div>
          </div>

          {/* Scrollable content: chat messages then results */}
          <div
            ref={chatScrollRef}
            className="flex-1 overflow-y-auto bg-social-bg"
            style={{ scrollBehavior: 'smooth' }}
          >
            {/* Chat messages — centered, max-width */}
            <div
              className="mx-auto flex flex-col gap-[14px]"
              style={{ maxWidth: 720, padding: '28px 24px 0' }}
            >
              {messages.map((msg) => (
                <ChatMessage
                  key={msg.id}
                  message={msg}
                  onGoalSelect={handleGoalSelect}
                  onTopicSubmit={handleTopicSubmit}
                  selectedGoal={selectedGoal}
                  isGenerating={isGenerating}
                  platformPickerState={{
                    selected: selectedPlatforms,
                    onToggle: togglePlatform,
                    onConfirm: handlePlatformConfirm,
                    isGenerating,
                  }}
                  calendarSetupState={{
                    timeAmount: calendarTimeAmount,
                    timeUnit: calendarTimeUnit,
                    scheduleType: calendarScheduleType,
                    postsPerPeriod: calendarPostsPerPeriod,
                    postsPerDay: calendarPostsPerDay,
                    selectedPlatforms,
                    seedIdeas: calendarSeedIdeas,
                    onTimeAmountChange: setCalendarTimeAmount,
                    onTimeUnitChange: setCalendarTimeUnit,
                    onScheduleTypeChange: setCalendarScheduleType,
                    onPostsPerPeriodChange: setCalendarPostsPerPeriod,
                    onPostsPerDayChange: setCalendarPostsPerDay,
                    onPlatformToggle: togglePlatform,
                    onSeedIdeasChange: setCalendarSeedIdeas,
                    onConfirm: () => {
                      /* legacy — not used in new flow */
                    },
                    isGenerating,
                  }}
                  ideasFlowProps={{
                    flow: ideasFlow,
                    directionChips: ideasDirectionChips,
                    audiencePainChips: ideasAudiencePainChips,
                    styleChips: ideasStyleChips,
                    hookChips: ideasHookChips,
                    inspirationItems,
                    isLoadingInspiration: inspirationLoading,
                    hasMoreInspiration: inspirationHasMore,
                    onDirectionSelect: handleIdeasDirectionSelect,
                    onDirectionOverrideChange: (v) => updateIdeasFlow({ directionOverride: v }),
                    onAudiencePainToggle: (chip) =>
                      updateIdeasFlow({
                        audiencePain: ideasFlow.audiencePain.includes(chip)
                          ? ideasFlow.audiencePain.filter((c) => c !== chip)
                          : [...ideasFlow.audiencePain, chip],
                      }),
                    onAudiencePainOverrideChange: (v) =>
                      updateIdeasFlow({ audiencePainOverride: v }),
                    onAudienceNext: handleIdeasAudienceNext,
                    onStyleToggle: (style) =>
                      updateIdeasFlow({
                        style: ideasFlow.style.includes(style)
                          ? ideasFlow.style.filter((s) => s !== style)
                          : [...ideasFlow.style, style],
                      }),
                    onStyleNext: handleIdeasStyleNext,
                    onHookToggle: (hook) =>
                      updateIdeasFlow({
                        hookPreference: ideasFlow.hookPreference.includes(hook)
                          ? ideasFlow.hookPreference.filter((h) => h !== hook)
                          : [...ideasFlow.hookPreference, hook],
                      }),
                    onHookNext: handleIdeasHookNext,
                    onInspirationModeChange: handleIdeasInspirationModeChange,
                    onInspirationItemToggle: handleIdeasInspirationItemToggle,
                    onInspirationSeedChange: (v) => updateIdeasFlow({ inspirationSeed: v }),
                    onInspirationNext: handleIdeasInspirationNext,
                    onLoadMoreInspiration: handleLoadMoreInspiration,
                    onConstraintsChange: (v) => updateIdeasFlow({ constraints: v }),
                    onConstraintsNext: handleIdeasConstraintsNext,
                    onGenerate: () => generateIdeas(ideasFlow),
                    isGenerating,
                  }}
                  calendarFlowProps={{
                    plan: calendarPlan,
                    brandSettings,
                    brandPillars,
                    suggestedPlatform,
                    suggestedGoal,
                    hookStrategyChips,
                    interactionStyleChips,
                    inspirationItems,
                    isLoadingInspiration: inspirationLoading,
                    hasMoreInspiration: inspirationHasMore,
                    onStep1Next: handleCalendarStep1Next,
                    onTimeframeValueChange: (v) => updateCalendarPlan({ timeframeValue: v }),
                    onTimeframeUnitChange: (u) => updateCalendarPlan({ timeframeUnit: u }),
                    onCadenceTypeChange: (t) => updateCalendarPlan({ cadenceType: t }),
                    onCadenceValueChange: (v) => updateCalendarPlan({ cadenceValue: v }),
                    onPlatformPrimarySelect: handleCalendarPlatformPrimarySelect,
                    onRepurposeToggle: handleCalendarRepurposeToggle,
                    onPlatformPrimaryNext: handleCalendarPlatformPrimaryNext,
                    onPrimaryGoalSelect: handleCalendarPrimaryGoalSelect,
                    onSecondaryGoalChange: (v) => updateCalendarPlan({ secondaryGoal: v }),
                    onGoalNext: handleCalendarGoalNext,
                    onPillarModeChange: handleCalendarPillarModeChange,
                    onPillarWeightChange: handleCalendarPillarWeightChange,
                    onPillarIntentChange: handleCalendarPillarIntentChange,
                    onPillarsNext: handleCalendarPillarsNext,
                    onContentTypeChange: handleCalendarContentTypeChange,
                    onContentTypeNext: handleCalendarContentTypeNext,
                    onHookStrategyToggle: handleCalendarHookStrategyToggle,
                    onHookStrategyNext: handleCalendarHookStrategyNext,
                    onInteractionStyleToggle: handleCalendarInteractionStyleToggle,
                    onInteractionStyleNext: handleCalendarInteractionStyleNext,
                    onVariationSelect: handleCalendarVariationSelect,
                    onVariationNext: handleCalendarVariationNext,
                    onInspirationModeChange: handleCalendarInspirationModeChange,
                    onInspirationItemToggle: handleInspirationItemToggle,
                    onInspirationSeedTextChange: (v) => updateCalendarPlan({ inspirationSeed: v }),
                    onLoadMoreInspiration: handleLoadMoreInspiration,
                    onInspirationNext: handleCalendarInspirationNext,
                    onIncludeToggle: handleCalendarIncludeToggle,
                    onAvoidToggle: handleCalendarAvoidToggle,
                    onConstraintsChange: (v) => updateCalendarPlan({ constraints: v }),
                    onConstraintsNext: handleCalendarConstraintsNext,
                    onGenerate: handleCalendarGenerate,
                    isGenerating,
                  }}
                  onChipClick={handleChipClick}
                />
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Results section — full width, below chat */}
            {outputMode !== 'empty' && (
              <div className="mx-auto flex flex-col gap-[14px] mt-10" style={{ maxWidth: 720 }}>
                {/* Results header — sticky */}
                <div
                  className="sticky top-0 z-10 flex lg:flex-row flex-col lg:items-center items-left lg:gap-1 gap-2 justify-between bg-social-bg"
                  style={{ padding: '10px 24px' }}
                >
                  <div
                    className="pointer-events-none absolute -bottom-6 left-0 right-0 h-8 z-5"
                    style={{
                      background:
                        'linear-gradient(to bottom, var(--social-bg), rgba(var(--social-bg-rgb), 0))',
                    }}
                  />
                  <div>
                    <p className="text-[13px] font-medium text-foreground font-satoshi">
                      {outputTitle}
                    </p>
                    <p className="text-[8px] text-muted-foreground mt-0.5 font-mono">
                      {outputSubtitle}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 lg:w-fit w-full">
                    <div className="hidden lg:flex items-center gap-1 ">
                      {(['ideas', 'saved'] as const).map((tab) => (
                        <button
                          key={tab}
                          type="button"
                          onClick={() => setActiveOutputTab(tab === 'saved' ? 'saved' : 'ideas')}
                          className={cn(
                            'font-mono px-2.5 py-1 rounded-md text-[12px] font-medium transition-colors',
                            activeOutputTab === tab
                              ? 'bg-muted text-foreground'
                              : 'text-muted-foreground',
                          )}
                        >
                          {tab === 'ideas'
                            ? outputMode === 'calendar'
                              ? 'Calendar'
                              : 'Ideas'
                            : `Saved (${totalSaved})`}
                        </button>
                      ))}
                    </div>
                    <Button
                      variant="default"
                      size="sm"
                      className="lg:gap-1.5 text-[10px] font-mono py-1 px-2 h-auto lg:w-fit w-full"
                      onClick={handleSaveAllDrafts}
                      disabled={totalSaved === totalGenerated}
                    >
                      <FloppyIcon size={10} /> Save all as drafts
                    </Button>
                  </div>
                </div>

                {/* Results content */}
                <div style={{ padding: '20px 24px' }}>
                  {outputMode === 'ideas' && activeOutputTab === 'ideas' && (
                    <div className="flex flex-col gap-3">
                      {ideaCards.map((card, i) => (
                        <IdeaCard
                          key={card.id}
                          card={card}
                          animationDelay={i * 0.06}
                          onRegenerate={handleRegenerateCard}
                          onDelete={handleDeleteCard}
                          onSaveDraft={handleSaveDraft}
                          onOpenComposer={handleOpenComposer}
                        />
                      ))}
                    </div>
                  )}

                  {outputMode === 'ideas' && activeOutputTab === 'saved' && (
                    <div className="flex flex-col gap-3">
                      {ideaCards.filter((c) => c.saved).length === 0 ? (
                        <p className="text-[13px] text-muted-foreground text-center py-12">
                          No saved drafts yet. Save ideas using the &quot;Save draft&quot; button on
                          each card.
                        </p>
                      ) : (
                        ideaCards
                          .filter((c) => c.saved)
                          .map((card, i) => (
                            <IdeaCard
                              key={card.id}
                              card={card}
                              animationDelay={i * 0.06}
                              onRegenerate={handleRegenerateCard}
                              onDelete={handleDeleteCard}
                              onSaveDraft={handleSaveDraft}
                              onOpenComposer={handleOpenComposer}
                            />
                          ))
                      )}
                    </div>
                  )}

                  {outputMode === 'calendar' && activeOutputTab === 'ideas' && (
                    <div>
                      <PillarDonutChart posts={calendarPosts} />
                      <div className="flex flex-col">
                        {calendarDays.map((day) => (
                          <div key={day}>
                            <p
                              className="text-[10px] uppercase font-medium text-muted-foreground"
                              style={{ padding: '10px 0 6px', letterSpacing: '0.06em' }}
                            >
                              {day}
                            </p>
                            <div className="flex flex-col gap-3">
                              {calendarByDay[day].map((post) => (
                                <CalendarPostCard
                                  key={post.id}
                                  post={post}
                                  onRegenerate={handleRegenerateCalendarPost}
                                  onSaveDraft={handleSaveCalendarDraft}
                                  onOpenComposer={handleOpenCalendarComposer}
                                />
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {outputMode === 'calendar' && activeOutputTab === 'saved' && (
                    <div className="flex flex-col gap-3">
                      {calendarPosts.filter((p) => p.saved).length === 0 ? (
                        <p className="text-[13px] text-muted-foreground text-center py-12">
                          No saved drafts yet.
                        </p>
                      ) : (
                        calendarPosts
                          .filter((p) => p.saved)
                          .map((post) => (
                            <CalendarPostCard
                              key={post.id}
                              post={post}
                              onRegenerate={handleRegenerateCalendarPost}
                              onSaveDraft={handleSaveCalendarDraft}
                              onOpenComposer={handleOpenCalendarComposer}
                            />
                          ))
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="h-8" />
          </div>
        </div>
      )}
    </>
  );
}
